import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Panel,
  MarkerType,
  Handle,
  Position,
  NodeProps,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Save,
  X,
  MessageSquare,
  HelpCircle,
  Info,
  Search,
  Wrench,
  GitBranch,
  Menu,
  PhoneForwarded,
  PhoneOff,
  Globe,
  GripVertical,
  Trash2,
  FileText,
  AlertTriangle,
  Brain,
  Database,
  LayoutTemplate,
  Variable,
  ChevronDown,
  Sparkles,
  BookOpen,
  Phone,
  Users,
  Calendar,
  ClipboardCheck,
  Headphones,
  Contact,
  Send,
  Tag,
  Webhook,
  Plus,
  ShieldAlert,
  Package,
  UserPlus,
  ClipboardList,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@easyflow/ui-frontend/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import DesktopOnlyOverlay from '@/components/layout/DesktopOnlyOverlay';

export type AgentType = 'openai' | 'gemini' | 'elevenlabs';

interface FlowNodeData {
  type: string;
  label: string;
  message?: string;
  variable?: string;
  kbId?: string;
  kbQuery?: string;
  toolName?: string;
  toolParams?: Record<string, string>;
  condition?: string;
  trueBranch?: string;
  falseBranch?: string;
  ivrOptions?: Array<{ dtmf: string; label: string; nextNodeId?: string }>;
  transferTarget?: string;
  transferType?: string;
  closingMessage?: string;
  googleSearchEnabled?: boolean;
  codeExecutionEnabled?: boolean;
  description?: string;
  memoryAction?: 'save' | 'search' | 'recall';
  memoryScope?: 'run' | 'workflow' | 'tenant' | 'customer' | 'agent';
  memoryKey?: string;
  memoryValue?: string;
  memoryRetention?: 'session' | 'short' | 'long' | 'permanent';
  memoryCategory?: string;
  searchQuery?: string;
  searchMinSimilarity?: number;
  searchMaxResults?: number;
  searchSource?: 'memory' | 'kb' | 'both';
  searchReranking?: boolean;
  crmAction?: 'create_lead' | 'create_task' | 'update_contact';
  crmFields?: Record<string, string>;
  sendChannel?: 'whatsapp' | 'sms' | 'email';
  sendTo?: string;
  sendBody?: string;
  sendSubject?: string;
  varName?: string;
  varValue?: string;
  apiUrl?: string;
  apiMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  apiHeaders?: Record<string, string>;
  apiBody?: string;
  apiResultVar?: string;
}

export interface FlowDefinition {
  nodes: Node<FlowNodeData>[];
  edges: Edge[];
  version: number;
}

interface VoiceAgentFlowBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  agentType: AgentType;
  currentPrompt: string;
  flowDefinition?: FlowDefinition | null;
  onSave: (prompt: string, flowDef: FlowDefinition) => void;
  agentName?: string;
  agentId?: string;
}

const NODE_TYPES_CONFIG = [
  { type: 'greeting', label: 'Saluto', icon: MessageSquare, color: 'from-emerald-50 to-emerald-100 border-emerald-400', iconBg: 'bg-emerald-500', mapColor: '#10b981', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'question', label: 'Domanda', icon: HelpCircle, color: 'from-blue-50 to-blue-100 border-blue-400', iconBg: 'bg-blue-500', mapColor: '#3b82f6', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'kb_lookup', label: 'KB Lookup', icon: Search, color: 'from-violet-50 to-violet-100 border-violet-400', iconBg: 'bg-violet-500', mapColor: '#8b5cf6', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'tool_action', label: 'Tool Action', icon: Wrench, color: 'from-amber-50 to-amber-100 border-amber-400', iconBg: 'bg-amber-500', mapColor: '#f59e0b', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'condition', label: 'Condizione', icon: GitBranch, color: 'from-orange-50 to-orange-100 border-orange-400', iconBg: 'bg-orange-500', mapColor: '#f97316', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'ivr_menu', label: 'Menu IVR', icon: Menu, color: 'from-cyan-50 to-cyan-100 border-cyan-400', iconBg: 'bg-cyan-500', mapColor: '#06b6d4', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'context_memory', label: 'Memoria Contesto', icon: Brain, color: 'from-pink-50 to-pink-100 border-pink-400', iconBg: 'bg-pink-500', mapColor: '#ec4899', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'semantic_memory', label: 'Memoria Semantica', icon: Database, color: 'from-violet-50 to-violet-100 border-violet-400', iconBg: 'bg-violet-600', mapColor: '#7c3aed', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'semantic_search', label: 'Ricerca Semantica', icon: Database, color: 'from-purple-50 to-purple-100 border-purple-400', iconBg: 'bg-purple-500', mapColor: '#a855f7', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'transfer', label: 'Trasferisci', icon: PhoneForwarded, color: 'from-indigo-50 to-indigo-100 border-indigo-400', iconBg: 'bg-indigo-500', mapColor: '#6366f1', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'closure', label: 'Chiusura', icon: PhoneOff, color: 'from-rose-50 to-rose-100 border-rose-400', iconBg: 'bg-rose-500', mapColor: '#f43f5e', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'crm_action', label: 'Azione CRM', icon: Contact, color: 'from-lime-50 to-lime-100 border-lime-400', iconBg: 'bg-lime-600', mapColor: '#65a30d', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'send_message', label: 'Invia Messaggio', icon: Send, color: 'from-sky-50 to-sky-100 border-sky-400', iconBg: 'bg-sky-500', mapColor: '#0ea5e9', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'set_variable', label: 'Imposta Variabile', icon: Tag, color: 'from-fuchsia-50 to-fuchsia-100 border-fuchsia-400', iconBg: 'bg-fuchsia-500', mapColor: '#d946ef', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'api_call', label: 'Chiamata API', icon: Webhook, color: 'from-stone-50 to-stone-100 border-stone-400', iconBg: 'bg-stone-600', mapColor: '#57534e', agents: ['openai', 'gemini', 'elevenlabs'] },
  { type: 'google_tool', label: 'Google Tool', icon: Globe, color: 'from-teal-50 to-teal-100 border-teal-400', iconBg: 'bg-teal-500', mapColor: '#14b8a6', agents: ['gemini'] },
];

interface VariableCategoryItem {
  category: string;
  icon: typeof Users;
  variables: { key: string; label: string; example?: string }[];
}

const SYSTEM_INTERNAL_VARS = new Set([
  'tenant_id', 'tenant_name', 'workflow_id', 'run_id', 'timestamp',
  'ai_model', 'ai_prompt', 'ai_response', 'ai_system_prompt', 'ai_tokens_used',
  'ai_voice_id', 'business_hours_status', 'current_schedule',
  'kb_query', 'kb_results', 'kb_sources',
  'memory_content', 'memory_count', 'memory_id', 'memory_namespace',
  'memory_query', 'memory_results', 'recording_status', 'audio_file',
  'call_recording_path', 'call_hangup_cause', 'form_data', 'form_name', 'form_source',
]);

const FALLBACK_VARIABLE_CATEGORIES: VariableCategoryItem[] = [
  {
    category: 'Chiamata',
    icon: Phone,
    variables: [
      { key: 'caller_number', label: 'Numero chiamante', example: '+39 333 9876543' },
      { key: 'caller_name', label: 'Nome chiamante', example: 'Mario Rossi' },
      { key: 'call_direction', label: 'Direzione', example: 'inbound' },
      { key: 'call_date', label: 'Data chiamata', example: '03/04/2026' },
      { key: 'call_duration', label: 'Durata chiamata', example: '5:30' },
      { key: 'queue_name', label: 'Coda', example: 'Assistenza' },
      { key: 'department_name', label: 'Reparto', example: 'Vendite' },
      { key: 'dtmf_digits', label: 'Tasti premuti (DTMF)', example: '1' },
    ]
  },
  {
    category: 'Agente & Contesto',
    icon: Variable,
    variables: [
      { key: 'agent_name', label: 'Nome agente AI', example: 'Sofia' },
      { key: 'current_date', label: 'Data corrente', example: '03/04/2026' },
      { key: 'current_time', label: 'Ora corrente', example: '14:30' },
      { key: 'current_day_of_week', label: 'Giorno settimana', example: 'Giovedi' },
    ]
  },
  {
    category: 'Cliente',
    icon: Users,
    variables: [
      { key: 'customer_first_name', label: 'Nome', example: 'Mario' },
      { key: 'customer_last_name', label: 'Cognome', example: 'Rossi' },
      { key: 'customer_full_name', label: 'Nome completo', example: 'Mario Rossi' },
      { key: 'customer_phone', label: 'Telefono', example: '+39 333 1234567' },
      { key: 'customer_email', label: 'Email', example: 'mario@email.it' },
      { key: 'customer_company', label: 'Azienda', example: 'Rossi SRL' },
    ]
  },
  {
    category: 'Azienda',
    icon: Globe,
    variables: [
      { key: 'company_name', label: 'Nome azienda', example: 'Acme SPA' },
      { key: 'company_phone', label: 'Telefono azienda', example: '+39 02 1234567' },
      { key: 'company_email', label: 'Email azienda', example: 'info@acme.it' },
      { key: 'company_city', label: 'Citta', example: 'Milano' },
    ]
  },
  {
    category: 'Appuntamento',
    icon: Calendar,
    variables: [
      { key: 'appointment_date', label: 'Data', example: '15/04/2026' },
      { key: 'appointment_start_time', label: 'Ora inizio', example: '14:30' },
      { key: 'service_type', label: 'Tipo', example: 'Consulenza' },
      { key: 'appointment_duration', label: 'Durata (min)', example: '30' },
      { key: 'appointment_title', label: 'Titolo', example: 'Riunione progetto' },
      { key: 'appointment_status', label: 'Stato', example: 'confermato' },
    ]
  },
  {
    category: 'Lead',
    icon: Sparkles,
    variables: [
      { key: 'lead_contact_name', label: 'Nome contatto', example: 'Anna Bianchi' },
      { key: 'lead_contact_phone', label: 'Telefono', example: '+39 02 1234567' },
      { key: 'lead_title', label: 'Titolo lead', example: 'Richiesta preventivo' },
      { key: 'lead_source', label: 'Fonte', example: 'sito web' },
      { key: 'lead_stage', label: 'Fase', example: 'Qualificato' },
      { key: 'lead_value', label: 'Valore', example: '5000' },
    ]
  },
];

const CATEGORY_ICON_MAP: Record<string, typeof Users> = {
  customer_b2c: Users,
  customer_b2b: Users,
  company_b2b: Globe,
  lead: Sparkles,
  system: Variable,
  call: Phone,
  appointment: Calendar,
  task: ClipboardCheck,
  videocall: Phone,
  warehouse: Database,
  hr: Users,
};

function useAvailableVariables(): { categories: VariableCategoryItem[]; loading: boolean } {
  const [categories, setCategories] = useState<VariableCategoryItem[]>(FALLBACK_VARIABLE_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      try {
        const response = await apiClient.request('/workflows/available-variables') as { data?: { variables?: Array<{ path: string; description?: string; example?: string; source_node_label?: string; category_key?: string; source?: string; type?: string }> } } | null;
        if (response?.data?.variables && Array.isArray(response.data.variables)) {
          const grouped = new Map<string, { label: string; icon: typeof Users; vars: { key: string; label: string; example?: string }[] }>();

          const validVars = response.data.variables.filter(
            (v): v is typeof v & { path: string } => typeof v.path === 'string' && v.path.length > 0 && !SYSTEM_INTERNAL_VARS.has(v.path)
          );
          for (const v of validVars) {
            const catLabel = v.source_node_label || v.source || 'Sistema';
            const catKey = v.category_key || v.source || 'system';
            if (!grouped.has(catLabel)) {
              grouped.set(catLabel, {
                label: catLabel,
                icon: CATEGORY_ICON_MAP[catKey] || Variable,
                vars: [],
              });
            }
            grouped.get(catLabel)!.vars.push({
              key: v.path,
              label: v.description || v.path,
              example: v.example || undefined,
            });
          }

          if (grouped.size > 0) {
            const apiCategories: VariableCategoryItem[] = Array.from(grouped.entries()).map(([label, data]) => ({
              category: label,
              icon: data.icon,
              variables: data.vars,
            }));
            setCategories(apiCategories);
          }
        }
      } catch {
        // API failed — keep fallback categories
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { categories, loading };
}

interface AgentTool {
  name: string;
  description?: string;
  parameters?: Record<string, { type?: string; description?: string }>;
}

interface KnowledgeBase {
  id: string;
  name: string;
}

interface ToolConfigItem {
  name?: string;
  description?: string;
  parameters?: Record<string, { type?: string; description?: string }>;
  function?: {
    name?: string;
    description?: string;
    parameters?: { properties?: Record<string, { type?: string; description?: string }> };
  };
}

interface VoiceAgentApiResponse {
  success?: boolean;
  data?: {
    id?: string;
    name?: string;
    tools_config?: ToolConfigItem[];
    realtime_config?: { tools?: ToolConfigItem[] };
    available_tools?: string[];
  };
}

interface VoiceAgentListResponse {
  success?: boolean;
  data?: Array<{
    id?: string;
    name?: string;
    tools_config?: ToolConfigItem[];
    realtime_config?: { tools?: ToolConfigItem[] };
    available_tools?: string[];
  }>;
}

interface KBOptionsResponse {
  options?: Array<{ value: string; label: string }>;
}

function extractToolsFromAgentData(agent: {
  tools_config?: ToolConfigItem[];
  realtime_config?: { tools?: ToolConfigItem[] };
  available_tools?: string[];
}): AgentTool[] {
  const tools: AgentTool[] = [];
  const seenNames = new Set<string>();

  const toolsConfig = agent.tools_config || agent.realtime_config?.tools || [];
  if (Array.isArray(toolsConfig)) {
    for (const t of toolsConfig) {
      const toolName = t.name || t.function?.name;
      if (toolName && !seenNames.has(toolName)) {
        seenNames.add(toolName);
        let params: Record<string, { type?: string; description?: string }> = t.parameters || t.function?.parameters?.properties || {};
        if (params && typeof params === 'object' && 'properties' in params) {
          const nested = (params as unknown as { properties?: Record<string, { type?: string; description?: string }> }).properties;
          if (nested && typeof nested === 'object') {
            params = nested;
          }
        }
        tools.push({
          name: toolName,
          description: t.description || t.function?.description || '',
          parameters: params,
        });
      }
    }
  }

  if (agent.available_tools && Array.isArray(agent.available_tools)) {
    for (const toolName of agent.available_tools) {
      if (typeof toolName === 'string' && !seenNames.has(toolName)) {
        seenNames.add(toolName);
        tools.push({ name: toolName, description: '', parameters: {} });
      }
    }
  }

  return tools;
}

function getAgentEndpoint(agentType: AgentType, agentId: string): string {
  switch (agentType) {
    case 'gemini':
      return `/gemini-voice/agents/${agentId}`;
    case 'elevenlabs':
      return `/elevenlabs/agents/${agentId}`;
    case 'openai':
    default:
      return `/ai/voice-agents/${agentId}`;
  }
}

function useAgentTools(agentType: AgentType, agentId?: string): { tools: AgentTool[]; loading: boolean } {
  const [tools, setTools] = useState<AgentTool[]>([]);
  const [loading, setLoading] = useState(true);
  const lastFetchKey = useRef<string>('');

  useEffect(() => {
    const fetchKey = `${agentType}:${agentId || ''}`;
    if (lastFetchKey.current === fetchKey) return;
    lastFetchKey.current = fetchKey;
    setLoading(true);

    let cancelled = false;
    (async () => {
      try {
        if (agentId) {
          const endpoint = getAgentEndpoint(agentType, agentId);
          const response = await apiClient.request(endpoint) as VoiceAgentApiResponse | null;
          if (!cancelled && response?.data) {
            setTools(extractToolsFromAgentData(response.data));
          }
        } else {
          const listEndpoints: Record<AgentType, string> = {
            openai: '/ai/voice-agents',
            gemini: '/gemini-voice/agents',
            elevenlabs: '/elevenlabs/agents',
          };
          const endpoint = listEndpoints[agentType] || listEndpoints.openai;
          const response = await apiClient.request(endpoint) as VoiceAgentListResponse | null;
          if (!cancelled && response?.data && Array.isArray(response.data)) {
            const allTools: AgentTool[] = [];
            const seenNames = new Set<string>();
            for (const agent of response.data) {
              for (const tool of extractToolsFromAgentData(agent)) {
                if (!seenNames.has(tool.name)) {
                  seenNames.add(tool.name);
                  allTools.push(tool);
                }
              }
            }
            setTools(allTools);
          }
        }
      } catch (err) {
        console.warn('[FlowBuilder] Failed to load agent tools:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [agentType, agentId]);

  return { tools, loading };
}

function useKnowledgeBases(): { knowledgeBases: KnowledgeBase[]; loading: boolean } {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const response = await apiClient.request('/workflows/options/knowledge-bases') as KBOptionsResponse | null;
        if (!cancelled && response?.options && Array.isArray(response.options)) {
          setKnowledgeBases(response.options.map((opt) => {
            const agentId = opt.value.startsWith('agent:') ? opt.value.substring(6) : opt.value;
            return {
              id: agentId,
              name: opt.label.replace(/^[🎙️💬]\s*/, ''),
            };
          }));
        }
      } catch (err) {
        console.warn('[FlowBuilder] Failed to load knowledge bases:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return { knowledgeBases, loading };
}

interface TransferDestination {
  value: string;
  label: string;
  group: string;
}

function useTransferDestinations(): { destinations: TransferDestination[]; loading: boolean } {
  const [destinations, setDestinations] = useState<TransferDestination[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const [extRes, queueRes] = await Promise.all([
          apiClient.get<{ extension?: string; display_name?: string; name?: string }[]>('/voip/sip-extensions').catch(() => ({ data: [] as { extension?: string; display_name?: string; name?: string }[] })),
          apiClient.get<{ extension?: string; name?: string }[]>('/voip/queues').catch(() => ({ data: [] as { extension?: string; name?: string }[] })),
        ]);

        if (cancelled) return;

        const dests: TransferDestination[] = [];

        const extData = extRes?.data || [];
        const extensions = Array.isArray(extData) ? extData : [];
        for (const ext of extensions) {
          if (ext.extension) {
            dests.push({
              value: ext.extension,
              label: `${ext.extension} - ${ext.display_name || ext.name || ''}`.trim(),
              group: 'Interni',
            });
          }
        }

        const queueData = queueRes?.data || [];
        const queues = Array.isArray(queueData) ? queueData : [];
        for (const q of queues) {
          if (q.name || q.extension) {
            dests.push({
              value: q.extension || q.name || '',
              label: `${q.name || q.extension}${q.extension && q.name ? ` (${q.extension})` : ''}`,
              group: 'Code',
            });
          }
        }

        setDestinations(dests);
      } catch (err) {
        console.warn('[FlowBuilder] Failed to load transfer destinations:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return { destinations, loading };
}

interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  icon: typeof MessageSquare;
  color: string;
  nodes: Node<FlowNodeData>[];
  edges: Edge[];
}

const FLOW_TEMPLATES: FlowTemplate[] = [
  {
    id: 'customer_support',
    name: 'Assistenza Clienti',
    description: 'Flusso completo per assistenza clienti con KB, memoria e trasferimento operatore',
    icon: Headphones,
    color: 'bg-blue-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Benvenuto', message: 'Benvenuto al nostro servizio assistenza! Sono {{agent_name}}, come posso aiutarla oggi?' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'question', label: 'Richiesta Cliente', message: 'Mi descriva il suo problema o la sua richiesta.', variable: 'richiesta_cliente' } },
      { id: 'kb1', type: 'flowNode', position: { x: 300, y: 280 }, data: { type: 'kb_lookup', label: 'Cerca in KB', kbQuery: '{{richiesta_cliente}}' } },
      { id: 'mem1', type: 'flowNode', position: { x: 300, y: 420 }, data: { type: 'semantic_memory', label: 'Salva Contesto', memoryAction: 'save', memoryScope: 'customer', memoryCategory: 'operational', memoryKey: 'ultima_richiesta', memoryValue: '{{richiesta_cliente}}' } },
      { id: 'c1', type: 'flowNode', position: { x: 300, y: 560 }, data: { type: 'condition', label: 'Risposta Trovata?', condition: 'la KB ha fornito una risposta soddisfacente', trueBranch: 'Comunica la risposta', falseBranch: 'Trasferisci a operatore' } },
      { id: 'cl1', type: 'flowNode', position: { x: 100, y: 700 }, data: { type: 'closure', label: 'Chiusura OK', closingMessage: 'Sono felice di averla aiutata! C\'e altro di cui ha bisogno?' } },
      { id: 't1', type: 'flowNode', position: { x: 500, y: 700 }, data: { type: 'transfer', label: 'Operatore', transferTarget: 'coda assistenza', transferType: 'queue' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'q1', ...edgeDefaults() },
      { id: 'e2', source: 'q1', target: 'kb1', ...edgeDefaults() },
      { id: 'e3', source: 'kb1', target: 'mem1', ...edgeDefaults() },
      { id: 'e4', source: 'mem1', target: 'c1', ...edgeDefaults() },
      { id: 'e5', source: 'c1', target: 'cl1', sourceHandle: 'true', ...edgeDefaults() },
      { id: 'e6', source: 'c1', target: 't1', sourceHandle: 'false', ...edgeDefaults() },
    ],
  },
  {
    id: 'lead_qualification',
    name: 'Qualifica Lead',
    description: 'Raccolta informazioni e qualifica del potenziale cliente con salvataggio in memoria',
    icon: Users,
    color: 'bg-emerald-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Benvenuto', message: 'Buongiorno! Grazie per averci contattato. Sono {{agent_name}} di {{company_name}}.' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'question', label: 'Nome', message: 'Con chi ho il piacere di parlare?', variable: 'nome_contatto' } },
      { id: 'q2', type: 'flowNode', position: { x: 300, y: 280 }, data: { type: 'question', label: 'Esigenza', message: 'Cosa sta cercando? Come possiamo aiutarla?', variable: 'esigenza' } },
      { id: 'q3', type: 'flowNode', position: { x: 300, y: 420 }, data: { type: 'question', label: 'Budget', message: 'Ha gia un\'idea di budget per questo progetto?', variable: 'budget' } },
      { id: 'q4', type: 'flowNode', position: { x: 300, y: 560 }, data: { type: 'question', label: 'Tempistiche', message: 'Entro quando vorrebbe implementare la soluzione?', variable: 'tempistiche' } },
      { id: 'mem1', type: 'flowNode', position: { x: 300, y: 700 }, data: { type: 'semantic_memory', label: 'Salva Lead', memoryAction: 'save', memoryScope: 'customer', memoryCategory: 'profile', memoryKey: 'lead_qualificato', memoryValue: 'Nome: {{nome_contatto}}, Esigenza: {{esigenza}}, Budget: {{budget}}, Tempistiche: {{tempistiche}}' } },
      { id: 'cl1', type: 'flowNode', position: { x: 300, y: 840 }, data: { type: 'closure', label: 'Chiusura', closingMessage: 'Perfetto {{nome_contatto}}, ho tutte le informazioni necessarie. Un nostro consulente la ricontattera a breve. Grazie!' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'q1', ...edgeDefaults() },
      { id: 'e2', source: 'q1', target: 'q2', ...edgeDefaults() },
      { id: 'e3', source: 'q2', target: 'q3', ...edgeDefaults() },
      { id: 'e4', source: 'q3', target: 'q4', ...edgeDefaults() },
      { id: 'e5', source: 'q4', target: 'mem1', ...edgeDefaults() },
      { id: 'e6', source: 'mem1', target: 'cl1', ...edgeDefaults() },
    ],
  },
  {
    id: 'appointment_booking',
    name: 'Booking Appuntamenti',
    description: 'Prenotazione appuntamenti con verifica disponibilita e conferma',
    icon: Calendar,
    color: 'bg-violet-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Benvenuto', message: 'Buongiorno! Sono {{agent_name}}, posso aiutarla a fissare un appuntamento.' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'question', label: 'Tipo Servizio', message: 'Per quale servizio desidera prenotare?', variable: 'tipo_servizio' } },
      { id: 'q2', type: 'flowNode', position: { x: 300, y: 280 }, data: { type: 'question', label: 'Data Preferita', message: 'Ha una data e un orario di preferenza?', variable: 'data_preferita' } },
      { id: 'ta1', type: 'flowNode', position: { x: 300, y: 420 }, data: { type: 'tool_action', label: 'Verifica Disponibilita', toolName: 'check_availability', toolParams: { date: '{{data_preferita}}', service: '{{tipo_servizio}}' } } },
      { id: 'c1', type: 'flowNode', position: { x: 300, y: 560 }, data: { type: 'condition', label: 'Disponibile?', condition: 'lo slot richiesto e disponibile', trueBranch: 'Conferma appuntamento', falseBranch: 'Proponi alternative' } },
      { id: 'ta2', type: 'flowNode', position: { x: 100, y: 700 }, data: { type: 'tool_action', label: 'Crea Appuntamento', toolName: 'create_appointment', toolParams: { date: '{{data_preferita}}', service: '{{tipo_servizio}}' } } },
      { id: 'q3', type: 'flowNode', position: { x: 500, y: 700 }, data: { type: 'question', label: 'Altra Data', message: 'Purtroppo lo slot non e disponibile. Posso suggerirle una data alternativa?', variable: 'data_alternativa' } },
      { id: 'cl1', type: 'flowNode', position: { x: 300, y: 840 }, data: { type: 'closure', label: 'Conferma', closingMessage: 'Appuntamento confermato! Le invieremo un promemoria. Grazie e arrivederci!' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'q1', ...edgeDefaults() },
      { id: 'e2', source: 'q1', target: 'q2', ...edgeDefaults() },
      { id: 'e3', source: 'q2', target: 'ta1', ...edgeDefaults() },
      { id: 'e4', source: 'ta1', target: 'c1', ...edgeDefaults() },
      { id: 'e5', source: 'c1', target: 'ta2', sourceHandle: 'true', ...edgeDefaults() },
      { id: 'e6', source: 'c1', target: 'q3', sourceHandle: 'false', ...edgeDefaults() },
      { id: 'e7', source: 'ta2', target: 'cl1', ...edgeDefaults() },
    ],
  },
  {
    id: 'faq_kb',
    name: 'FAQ con Knowledge Base',
    description: 'Risposte automatiche da KB con ricerca semantica e fallback operatore',
    icon: BookOpen,
    color: 'bg-amber-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Benvenuto FAQ', message: 'Benvenuto! Posso rispondere alle sue domande sui nostri servizi e prodotti.' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'question', label: 'Domanda', message: 'Qual e la sua domanda?', variable: 'domanda_utente' } },
      { id: 'ss1', type: 'flowNode', position: { x: 300, y: 280 }, data: { type: 'semantic_search', label: 'Cerca Risposta', searchQuery: '{{domanda_utente}}', searchSource: 'both', searchMinSimilarity: 0.6, searchMaxResults: 5 } },
      { id: 'c1', type: 'flowNode', position: { x: 300, y: 420 }, data: { type: 'condition', label: 'Trovata?', condition: 'sono stati trovati risultati pertinenti con similarita >= 60%', trueBranch: 'Fornisci risposta', falseBranch: 'Nessun risultato' } },
      { id: 'mem1', type: 'flowNode', position: { x: 100, y: 560 }, data: { type: 'semantic_memory', label: 'Salva FAQ', memoryAction: 'save', memoryScope: 'tenant', memoryCategory: 'knowledge', memoryKey: 'faq_frequenti', memoryValue: 'Domanda: {{domanda_utente}}' } },
      { id: 't1', type: 'flowNode', position: { x: 500, y: 560 }, data: { type: 'transfer', label: 'Operatore FAQ', transferTarget: 'operatore', transferType: 'blind' } },
      { id: 'cl1', type: 'flowNode', position: { x: 100, y: 700 }, data: { type: 'closure', label: 'Fine FAQ', closingMessage: 'Spero di esserle stato utile! Ha altre domande?' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'q1', ...edgeDefaults() },
      { id: 'e2', source: 'q1', target: 'ss1', ...edgeDefaults() },
      { id: 'e3', source: 'ss1', target: 'c1', ...edgeDefaults() },
      { id: 'e4', source: 'c1', target: 'mem1', sourceHandle: 'true', ...edgeDefaults() },
      { id: 'e5', source: 'c1', target: 't1', sourceHandle: 'false', ...edgeDefaults() },
      { id: 'e6', source: 'mem1', target: 'cl1', ...edgeDefaults() },
    ],
  },
  {
    id: 'outbound_sales',
    name: 'Outbound Vendite',
    description: 'Chiamata outbound per presentazione prodotto/servizio con qualifica interesse',
    icon: Phone,
    color: 'bg-rose-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Presentazione', message: 'Buongiorno {{customer_first_name}}! Sono {{agent_name}} di {{company_name}}. La contatto per presentarle una novita che potrebbe interessarle.' } },
      { id: 'c1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'condition', label: 'Interessato?', condition: 'il cliente ha mostrato interesse a continuare la conversazione', trueBranch: 'Prosegui con la presentazione', falseBranch: 'Ringrazia e chiudi' } },
      { id: 'kb1', type: 'flowNode', position: { x: 100, y: 280 }, data: { type: 'kb_lookup', label: 'Info Prodotto', kbQuery: 'caratteristiche principali e prezzi del prodotto/servizio' } },
      { id: 'q1', type: 'flowNode', position: { x: 100, y: 420 }, data: { type: 'question', label: 'Feedback', message: 'Cosa ne pensa? Le interessa approfondire?', variable: 'feedback_cliente' } },
      { id: 'mem1', type: 'flowNode', position: { x: 100, y: 560 }, data: { type: 'semantic_memory', label: 'Salva Interesse', memoryAction: 'save', memoryScope: 'customer', memoryCategory: 'behavioral', memoryKey: 'interesse_cliente', memoryValue: 'Cliente: {{customer_first_name}}, Feedback: {{feedback_cliente}}' } },
      { id: 't1', type: 'flowNode', position: { x: 100, y: 700 }, data: { type: 'transfer', label: 'Commerciale', transferTarget: 'reparto vendite', transferType: 'attended' } },
      { id: 'cl1', type: 'flowNode', position: { x: 500, y: 280 }, data: { type: 'closure', label: 'Non Interessato', closingMessage: 'Capisco, la ringrazio per il suo tempo. Le auguro una buona giornata!' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'c1', ...edgeDefaults() },
      { id: 'e2', source: 'c1', target: 'kb1', sourceHandle: 'true', ...edgeDefaults() },
      { id: 'e3', source: 'c1', target: 'cl1', sourceHandle: 'false', ...edgeDefaults() },
      { id: 'e4', source: 'kb1', target: 'q1', ...edgeDefaults() },
      { id: 'e5', source: 'q1', target: 'mem1', ...edgeDefaults() },
      { id: 'e6', source: 'mem1', target: 't1', ...edgeDefaults() },
    ],
  },
  {
    id: 'post_sale_survey',
    name: 'Sondaggio Post-Vendita',
    description: 'Raccolta feedback post-vendita con valutazione soddisfazione e salvataggio',
    icon: ClipboardCheck,
    color: 'bg-teal-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Saluto', message: 'Buongiorno {{customer_first_name}}! La contatto per sapere come si trova con il nostro servizio.' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'question', label: 'Soddisfazione', message: 'Su una scala da 1 a 10, quanto e soddisfatto del servizio ricevuto?', variable: 'punteggio' } },
      { id: 'q2', type: 'flowNode', position: { x: 300, y: 280 }, data: { type: 'question', label: 'Cosa Migliorare', message: 'C\'e qualcosa che potremmo migliorare?', variable: 'suggerimenti' } },
      { id: 'q3', type: 'flowNode', position: { x: 300, y: 420 }, data: { type: 'question', label: 'Consiglio', message: 'Consiglierebbe il nostro servizio a un collega o amico?', variable: 'nps' } },
      { id: 'mem1', type: 'flowNode', position: { x: 300, y: 560 }, data: { type: 'semantic_memory', label: 'Salva Feedback', memoryAction: 'save', memoryScope: 'customer', memoryCategory: 'behavioral', memoryKey: 'feedback_post_vendita', memoryValue: 'Cliente: {{customer_first_name}}, Punteggio: {{punteggio}}/10, Suggerimenti: {{suggerimenti}}, NPS: {{nps}}' } },
      { id: 'cl1', type: 'flowNode', position: { x: 300, y: 700 }, data: { type: 'closure', label: 'Ringraziamento', closingMessage: 'La ringrazio molto per il suo feedback! Lo utilizzeremo per migliorare il nostro servizio. Buona giornata!' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'q1', ...edgeDefaults() },
      { id: 'e2', source: 'q1', target: 'q2', ...edgeDefaults() },
      { id: 'e3', source: 'q2', target: 'q3', ...edgeDefaults() },
      { id: 'e4', source: 'q3', target: 'mem1', ...edgeDefaults() },
      { id: 'e5', source: 'mem1', target: 'cl1', ...edgeDefaults() },
    ],
  },
  {
    id: 'gestione_reclamo',
    name: 'Gestione Reclamo',
    description: 'Reclamo con ricerca KB, valutazione gravita, creazione ticket CRM ed escalation',
    icon: ShieldAlert,
    color: 'bg-red-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Benvenuto', message: 'Buongiorno, sono {{agent_name}} del servizio clienti di {{company_name}}. Mi dica pure come posso aiutarla.' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'question', label: 'Descrizione Reclamo', message: 'Mi descriva il problema riscontrato nel dettaglio.', variable: 'descrizione_reclamo' } },
      { id: 'kb1', type: 'flowNode', position: { x: 300, y: 280 }, data: { type: 'kb_lookup', label: 'Cerca Soluzione', kbQuery: '{{descrizione_reclamo}}' } },
      { id: 'c1', type: 'flowNode', position: { x: 300, y: 420 }, data: { type: 'condition', label: 'Gravita Alta?', condition: 'il reclamo riguarda un problema grave o urgente (danno economico, sicurezza, disservizio prolungato)', trueBranch: 'Escalation immediata', falseBranch: 'Gestione standard' } },
      { id: 'crm1', type: 'flowNode', position: { x: 100, y: 560 }, data: { type: 'crm_action', label: 'Crea Ticket Urgente', crmAction: 'create_task', crmFields: { titolo: 'Reclamo URGENTE: {{descrizione_reclamo}}', cliente: '{{customer_full_name}}', telefono: '{{caller_number}}', priorita: 'alta' } } },
      { id: 'crm2', type: 'flowNode', position: { x: 500, y: 560 }, data: { type: 'crm_action', label: 'Crea Ticket Standard', crmAction: 'create_task', crmFields: { titolo: 'Reclamo: {{descrizione_reclamo}}', cliente: '{{customer_full_name}}', telefono: '{{caller_number}}', priorita: 'normale' } } },
      { id: 't1', type: 'flowNode', position: { x: 100, y: 700 }, data: { type: 'transfer', label: 'Responsabile', transferTarget: 'coda reclami', transferType: 'attended' } },
      { id: 'cl1', type: 'flowNode', position: { x: 500, y: 700 }, data: { type: 'closure', label: 'Chiusura', closingMessage: 'Ho registrato la sua segnalazione con un ticket. Verra ricontattato entro 24 ore. Grazie per la pazienza!' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'q1', ...edgeDefaults() },
      { id: 'e2', source: 'q1', target: 'kb1', ...edgeDefaults() },
      { id: 'e3', source: 'kb1', target: 'c1', ...edgeDefaults() },
      { id: 'e4', source: 'c1', target: 'crm1', sourceHandle: 'true', ...edgeDefaults() },
      { id: 'e5', source: 'c1', target: 'crm2', sourceHandle: 'false', ...edgeDefaults() },
      { id: 'e6', source: 'crm1', target: 't1', ...edgeDefaults() },
      { id: 'e7', source: 'crm2', target: 'cl1', ...edgeDefaults() },
    ],
  },
  {
    id: 'recall_appuntamento',
    name: 'Recall Appuntamento',
    description: 'Richiamo per conferma/modifica appuntamento con invio conferma via messaggio',
    icon: Calendar,
    color: 'bg-violet-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Recall', message: 'Buongiorno {{customer_first_name}}, sono {{agent_name}} di {{company_name}}. La contatto per ricordarle il suo appuntamento del {{appointment_date}} alle {{appointment_start_time}}.' } },
      { id: 'c1', type: 'flowNode', position: { x: 300, y: 150 }, data: { type: 'condition', label: 'Conferma?', condition: 'il cliente conferma di poter essere presente all\'appuntamento', trueBranch: 'Appuntamento confermato', falseBranch: 'Modifica o cancella' } },
      { id: 'sm1', type: 'flowNode', position: { x: 100, y: 300 }, data: { type: 'send_message', label: 'Conferma WhatsApp', sendChannel: 'whatsapp', sendTo: '{{caller_number}}', sendBody: 'Appuntamento confermato: {{appointment_date}} ore {{appointment_start_time}} - {{service_type}}. A presto!' } },
      { id: 'q1', type: 'flowNode', position: { x: 500, y: 300 }, data: { type: 'question', label: 'Nuova Data', message: 'Quando preferirebbe spostare l\'appuntamento?', variable: 'nuova_data' } },
      { id: 'ta1', type: 'flowNode', position: { x: 500, y: 450 }, data: { type: 'tool_action', label: 'Modifica Appuntamento', toolName: 'update_appointment', toolParams: { new_date: '{{nuova_data}}', appointment_id: '{{appointment_id}}' } } },
      { id: 'sm2', type: 'flowNode', position: { x: 500, y: 600 }, data: { type: 'send_message', label: 'Conferma Modifica', sendChannel: 'whatsapp', sendTo: '{{caller_number}}', sendBody: 'Appuntamento modificato: nuova data {{nuova_data}}. A presto!' } },
      { id: 'cl1', type: 'flowNode', position: { x: 100, y: 450 }, data: { type: 'closure', label: 'Chiusura OK', closingMessage: 'Perfetto, appuntamento confermato! Le abbiamo inviato un promemoria su WhatsApp. Arrivederci!' } },
      { id: 'cl2', type: 'flowNode', position: { x: 500, y: 750 }, data: { type: 'closure', label: 'Chiusura Modifica', closingMessage: 'Appuntamento aggiornato! Le abbiamo inviato la conferma su WhatsApp. Arrivederci!' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'c1', ...edgeDefaults() },
      { id: 'e2', source: 'c1', target: 'sm1', sourceHandle: 'true', ...edgeDefaults() },
      { id: 'e3', source: 'c1', target: 'q1', sourceHandle: 'false', ...edgeDefaults() },
      { id: 'e4', source: 'sm1', target: 'cl1', ...edgeDefaults() },
      { id: 'e5', source: 'q1', target: 'ta1', ...edgeDefaults() },
      { id: 'e6', source: 'ta1', target: 'sm2', ...edgeDefaults() },
      { id: 'e7', source: 'sm2', target: 'cl2', ...edgeDefaults() },
    ],
  },
  {
    id: 'supporto_tecnico_l1_l2',
    name: 'Supporto Tecnico L1→L2',
    description: 'Diagnosi tecnica con ricerca semantica, livello urgenza e escalation L2',
    icon: Headphones,
    color: 'bg-orange-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Benvenuto', message: 'Assistenza tecnica {{company_name}}, sono {{agent_name}}. Come posso aiutarla?' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'question', label: 'Problema', message: 'Mi descriva il problema tecnico che sta riscontrando.', variable: 'problema_tecnico' } },
      { id: 'ss1', type: 'flowNode', position: { x: 300, y: 280 }, data: { type: 'semantic_search', label: 'Diagnosi', searchQuery: '{{problema_tecnico}}', searchSource: 'both', searchMinSimilarity: 0.6, searchMaxResults: 5, searchReranking: true } },
      { id: 'c1', type: 'flowNode', position: { x: 300, y: 420 }, data: { type: 'condition', label: 'Risolto L1?', condition: 'la diagnosi ha trovato una soluzione applicabile e il problema e stato risolto', trueBranch: 'Problema risolto', falseBranch: 'Escalation L2' } },
      { id: 'cl1', type: 'flowNode', position: { x: 100, y: 560 }, data: { type: 'closure', label: 'Risolto', closingMessage: 'Sono contento che il problema sia risolto! Se dovesse ripresentarsi non esiti a richiamarci.' } },
      { id: 'sv1', type: 'flowNode', position: { x: 500, y: 560 }, data: { type: 'set_variable', label: 'Urgenza', varName: 'livello_urgenza', varValue: 'alta' } },
      { id: 't1', type: 'flowNode', position: { x: 500, y: 700 }, data: { type: 'transfer', label: 'Supporto L2', transferTarget: 'coda supporto L2', transferType: 'attended' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'q1', ...edgeDefaults() },
      { id: 'e2', source: 'q1', target: 'ss1', ...edgeDefaults() },
      { id: 'e3', source: 'ss1', target: 'c1', ...edgeDefaults() },
      { id: 'e4', source: 'c1', target: 'cl1', sourceHandle: 'true', ...edgeDefaults() },
      { id: 'e5', source: 'c1', target: 'sv1', sourceHandle: 'false', ...edgeDefaults() },
      { id: 'e6', source: 'sv1', target: 't1', ...edgeDefaults() },
    ],
  },
  {
    id: 'vendita_upsell',
    name: 'Vendita Upsell/Cross-sell',
    description: 'Proposta commerciale personalizzata con KB prodotti e aggiornamento lead CRM',
    icon: Package,
    color: 'bg-emerald-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Saluto Personalizzato', message: 'Buongiorno {{customer_first_name}}! Sono {{agent_name}} di {{company_name}}. Ho una proposta che potrebbe interessarle.' } },
      { id: 'kb1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'kb_lookup', label: 'Prodotti Consigliati', kbQuery: 'prodotti e offerte consigliate per cliente esistente' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 280 }, data: { type: 'question', label: 'Interesse', message: 'Cosa ne pensa? Le interessa approfondire questa opportunita?', variable: 'interesse_cliente' } },
      { id: 'c1', type: 'flowNode', position: { x: 300, y: 420 }, data: { type: 'condition', label: 'Interessato?', condition: 'il cliente ha mostrato interesse concreto per la proposta', trueBranch: 'Aggiorna lead e trasferisci', falseBranch: 'Ringrazia e chiudi' } },
      { id: 'crm1', type: 'flowNode', position: { x: 100, y: 560 }, data: { type: 'crm_action', label: 'Aggiorna Lead', crmAction: 'update_contact', crmFields: { interesse: '{{interesse_cliente}}', fase: 'qualificato', fonte: 'upsell telefonico' } } },
      { id: 't1', type: 'flowNode', position: { x: 100, y: 700 }, data: { type: 'transfer', label: 'Commerciale', transferTarget: 'reparto vendite', transferType: 'attended' } },
      { id: 'cl1', type: 'flowNode', position: { x: 500, y: 560 }, data: { type: 'closure', label: 'Non Interessato', closingMessage: 'Capisco, la ringrazio per il tempo dedicato. Restiamo a disposizione per qualsiasi esigenza futura!' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'kb1', ...edgeDefaults() },
      { id: 'e2', source: 'kb1', target: 'q1', ...edgeDefaults() },
      { id: 'e3', source: 'q1', target: 'c1', ...edgeDefaults() },
      { id: 'e4', source: 'c1', target: 'crm1', sourceHandle: 'true', ...edgeDefaults() },
      { id: 'e5', source: 'c1', target: 'cl1', sourceHandle: 'false', ...edgeDefaults() },
      { id: 'e6', source: 'crm1', target: 't1', ...edgeDefaults() },
    ],
  },
  {
    id: 'welcome_call',
    name: 'Welcome Call Nuovo Cliente',
    description: 'Benvenuto nuovo cliente con raccolta feedback, salvataggio permanente e invio WhatsApp',
    icon: UserPlus,
    color: 'bg-cyan-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Benvenuto', message: 'Buongiorno {{customer_first_name}}! Sono {{agent_name}} di {{company_name}}. La chiamo per darle il benvenuto come nostro nuovo cliente!' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 140 }, data: { type: 'question', label: 'Feedback Iniziale', message: 'Come si trova finora con il nostro servizio? C\'e qualcosa che possiamo migliorare?', variable: 'feedback_iniziale' } },
      { id: 'sv1', type: 'flowNode', position: { x: 300, y: 280 }, data: { type: 'set_variable', label: 'Soddisfazione', varName: 'soddisfazione', varValue: '{{feedback_iniziale}}' } },
      { id: 'mem1', type: 'flowNode', position: { x: 300, y: 420 }, data: { type: 'semantic_memory', label: 'Salva Feedback', memoryAction: 'save', memoryScope: 'customer', memoryCategory: 'behavioral', memoryKey: 'welcome_feedback', memoryValue: 'Nuovo cliente: {{customer_full_name}}, Feedback: {{feedback_iniziale}}, Data: {{current_date}}' } },
      { id: 'sm1', type: 'flowNode', position: { x: 300, y: 560 }, data: { type: 'send_message', label: 'WhatsApp Benvenuto', sendChannel: 'whatsapp', sendTo: '{{caller_number}}', sendBody: 'Benvenuto in {{company_name}}, {{customer_first_name}}! Siamo felici di averla come cliente. Per qualsiasi necessita ci contatti al {{company_phone}}.' } },
      { id: 'cl1', type: 'flowNode', position: { x: 300, y: 700 }, data: { type: 'closure', label: 'Chiusura', closingMessage: 'Grazie {{customer_first_name}}! Le abbiamo inviato un messaggio di benvenuto su WhatsApp. Non esiti a contattarci per qualsiasi cosa. Buona giornata!' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'q1', ...edgeDefaults() },
      { id: 'e2', source: 'q1', target: 'sv1', ...edgeDefaults() },
      { id: 'e3', source: 'sv1', target: 'mem1', ...edgeDefaults() },
      { id: 'e4', source: 'mem1', target: 'sm1', ...edgeDefaults() },
      { id: 'e5', source: 'sm1', target: 'cl1', ...edgeDefaults() },
    ],
  },
  {
    id: 'raccolta_dati',
    name: 'Raccolta Dati Strutturata',
    description: 'Raccolta dati sequenziale con variabili, salvataggio CRM e conferma finale',
    icon: ClipboardList,
    color: 'bg-indigo-500',
    nodes: [
      { id: 'g1', type: 'flowNode', position: { x: 300, y: 0 }, data: { type: 'greeting', label: 'Benvenuto', message: 'Buongiorno, sono {{agent_name}} di {{company_name}}. Avrei bisogno di raccogliere alcune informazioni.' } },
      { id: 'q1', type: 'flowNode', position: { x: 300, y: 130 }, data: { type: 'question', label: 'Nome e Cognome', message: 'Mi puo dire il suo nome e cognome?', variable: 'nome_completo' } },
      { id: 'q2', type: 'flowNode', position: { x: 300, y: 260 }, data: { type: 'question', label: 'Email', message: 'Mi puo fornire il suo indirizzo email?', variable: 'email_contatto' } },
      { id: 'q3', type: 'flowNode', position: { x: 300, y: 390 }, data: { type: 'question', label: 'Azienda', message: 'Per quale azienda lavora?', variable: 'azienda' } },
      { id: 'q4', type: 'flowNode', position: { x: 300, y: 520 }, data: { type: 'question', label: 'Richiesta', message: 'Qual e il motivo della sua richiesta?', variable: 'motivo_richiesta' } },
      { id: 'sv1', type: 'flowNode', position: { x: 300, y: 650 }, data: { type: 'set_variable', label: 'Categoria', varName: 'categoria_richiesta', varValue: 'nuova_richiesta' } },
      { id: 'crm1', type: 'flowNode', position: { x: 300, y: 780 }, data: { type: 'crm_action', label: 'Salva nel CRM', crmAction: 'create_lead', crmFields: { nome: '{{nome_completo}}', email: '{{email_contatto}}', azienda: '{{azienda}}', richiesta: '{{motivo_richiesta}}', categoria: '{{categoria_richiesta}}', telefono: '{{caller_number}}' } } },
      { id: 'cl1', type: 'flowNode', position: { x: 300, y: 910 }, data: { type: 'closure', label: 'Conferma', closingMessage: 'Perfetto {{nome_completo}}, ho registrato tutte le informazioni. Un nostro operatore la ricontattera al piu presto. Grazie!' } },
    ],
    edges: [
      { id: 'e1', source: 'g1', target: 'q1', ...edgeDefaults() },
      { id: 'e2', source: 'q1', target: 'q2', ...edgeDefaults() },
      { id: 'e3', source: 'q2', target: 'q3', ...edgeDefaults() },
      { id: 'e4', source: 'q3', target: 'q4', ...edgeDefaults() },
      { id: 'e5', source: 'q4', target: 'sv1', ...edgeDefaults() },
      { id: 'e6', source: 'sv1', target: 'crm1', ...edgeDefaults() },
      { id: 'e7', source: 'crm1', target: 'cl1', ...edgeDefaults() },
    ],
  },
];

function edgeDefaults() {
  return {
    style: { strokeWidth: 2, stroke: '#6366f1' },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' },
    animated: true,
  };
}

function getNodeConfig(type: string) {
  return NODE_TYPES_CONFIG.find(n => n.type === type);
}

const SCOPE_COLORS: Record<string, string> = {
  run: 'bg-sky-100 text-sky-700 border-sky-300',
  workflow: 'bg-amber-100 text-amber-700 border-amber-300',
  tenant: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  customer: 'bg-violet-100 text-violet-700 border-violet-300',
  agent: 'bg-indigo-100 text-indigo-700 border-indigo-300',
};

const MEMORY_ACTION_LABELS: Record<string, string> = {
  save: 'Salva',
  search: 'Cerca',
  clear: 'Cancella',
  recall: 'Recupera',
};

function FlowNode({ data, selected }: NodeProps<FlowNodeData>) {
  const [showTip, setShowTip] = useState(false);
  const config = getNodeConfig(data.type);
  if (!config) return null;
  const Icon = config.icon;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define -- legacy guardrail #1219
  const tooltip = NODE_TOOLTIPS[data.type];

  return (
    <div
      className={`px-4 py-3 rounded-xl border-2 bg-gradient-to-br ${config.color} ${selected ? 'shadow-xl ring-2 ring-blue-500' : 'shadow-md'} min-w-[180px] max-w-[240px] group relative`}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      {showTip && tooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-gray-900 text-white text-[11px] leading-relaxed rounded-lg shadow-2xl z-50 pointer-events-none">
          <div className="font-bold text-xs mb-1 text-blue-300 flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5" />
            {config.label}
          </div>
          <div className="text-gray-200">{tooltip}</div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-gray-900" />
        </div>
      )}
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-3 !h-3 !border-2 !border-white" />
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 ${config.iconBg} rounded-lg`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-foreground text-sm truncate">{data.label}</div>
          <div className="text-xs text-muted-foreground capitalize">{config.label}</div>
        </div>
      </div>
      {data.message && (
        <div className="text-xs text-muted-foreground mt-1 truncate italic">"{data.message.substring(0, 40)}..."</div>
      )}
      {data.toolName && (
        <Badge variant="secondary" className="mt-1 text-xs">{data.toolName}</Badge>
      )}
      {data.type === 'condition' && data.condition && (
        <div className="text-xs text-orange-700 mt-1 font-mono truncate">if: {data.condition}</div>
      )}
      {data.type === 'context_memory' && (
        <div className="flex gap-1 mt-1">
          <Badge variant="outline" className="text-[10px] px-1">{MEMORY_ACTION_LABELS[data.memoryAction || 'save']}</Badge>
          <Badge variant="outline" className="text-[10px] px-1 bg-pink-100 text-pink-700">sessione</Badge>
        </div>
      )}
      {data.type === 'semantic_memory' && (
        <div className="flex gap-1 mt-1">
          <Badge variant="outline" className="text-[10px] px-1">{MEMORY_ACTION_LABELS[data.memoryAction || 'save']}</Badge>
          <Badge variant="outline" className={`text-[10px] px-1 ${SCOPE_COLORS[data.memoryScope || 'customer'] || 'bg-violet-100 text-violet-700'}`}>
            {data.memoryScope === 'agent' ? 'agente' : data.memoryScope === 'tenant' ? 'globale' : 'cliente'}
          </Badge>
        </div>
      )}
      {data.type === 'semantic_search' && (
        <div className="flex gap-1 mt-1">
          <Badge variant="outline" className="text-[10px] px-1 bg-purple-100 text-purple-700">{data.searchSource || 'both'}</Badge>
          {data.searchMinSimilarity && (
            <Badge variant="outline" className="text-[10px] px-1">{Math.round((data.searchMinSimilarity || 0.5) * 100)}%</Badge>
          )}
        </div>
      )}
      {data.type === 'crm_action' && (
        <div className="flex gap-1 mt-1 flex-wrap">
          <Badge variant="outline" className="text-[10px] px-1 bg-lime-100 text-lime-700">
            {data.crmAction === 'create_lead' ? 'Crea Lead' : data.crmAction === 'create_task' ? 'Crea Task' : data.crmAction === 'update_contact' ? 'Aggiorna Contatto' : 'CRM'}
          </Badge>
        </div>
      )}
      {data.type === 'send_message' && (
        <div className="flex gap-1 mt-1 flex-wrap">
          <Badge variant="outline" className="text-[10px] px-1 bg-sky-100 text-sky-700">
            {data.sendChannel === 'whatsapp' ? 'WhatsApp' : data.sendChannel === 'sms' ? 'SMS' : data.sendChannel === 'email' ? 'Email' : 'Messaggio'}
          </Badge>
          {data.sendTo && <Badge variant="outline" className="text-[10px] px-1 truncate max-w-[100px]">{data.sendTo}</Badge>}
        </div>
      )}
      {data.type === 'set_variable' && data.varName && (
        <div className="text-xs text-fuchsia-700 mt-1 font-mono truncate">{data.varName} = {data.varValue || '...'}</div>
      )}
      {data.type === 'api_call' && (
        <div className="flex gap-1 mt-1 flex-wrap">
          <Badge variant="outline" className="text-[10px] px-1 bg-stone-100 text-stone-700">{data.apiMethod || 'GET'}</Badge>
          {data.apiUrl && <Badge variant="outline" className="text-[10px] px-1 truncate max-w-[120px]">{data.apiUrl}</Badge>}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-gray-600 !w-3 !h-3 !border-2 !border-white" id="default" />
      {data.type === 'condition' && (
        <>
          <Handle type="source" position={Position.Right} className="!bg-emerald-500 !w-3 !h-3 !border-2 !border-white" id="true" style={{ top: '50%' }} />
          <Handle type="source" position={Position.Left} className="!bg-rose-500 !w-3 !h-3 !border-2 !border-white" id="false" style={{ top: '50%' }} />
        </>
      )}
    </div>
  );
}

const nodeTypes = {
  flowNode: FlowNode,
};

const defaultEdgeOptions = {
  style: { strokeWidth: 2, stroke: '#6366f1' },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' },
  animated: true,
};

function flowToPrompt(nodes: Node<FlowNodeData>[], edges: Edge[], variableCategories?: VariableCategoryItem[]): string {
  if (nodes.length === 0) return '';

  const lines: string[] = [];
  lines.push('# Istruzioni Conversazionali');
  lines.push('');
  lines.push('Segui questo flusso conversazionale nell\'ordine indicato:');
  lines.push('');

  const visited = new Set<string>();
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const edgesBySource = new Map<string, Edge[]>();
  for (const e of edges) {
    if (!edgesBySource.has(e.source)) edgesBySource.set(e.source, []);
    edgesBySource.get(e.source)!.push(e)!;
  }

  const roots = nodes.filter(n => !edges.some(e => e.target === n.id));
  const queue = roots.length > 0 ? [...roots] : [nodes[0]];
  let stepNum = 1;

  while (queue.length > 0) {
    const node = queue.shift();
    if (visited.has(node!.id!)) continue;
    visited.add(node!.id!);

    const d = node!.data!;
    lines.push(`## Passo ${stepNum}: ${d.label}`);

    switch (d.type) {
      case 'greeting':
        lines.push(`Saluta l'utente con: "${d.message || 'Benvenuto, come posso aiutarti?'}"`);
        break;
      case 'question':
        lines.push(`Chiedi all'utente: "${d.message || ''}"`);
        if (d.variable) lines.push(`Salva la risposta come: ${d.variable}`);
        break;
      case 'kb_lookup':
        lines.push(`Cerca nella Knowledge Base${d.kbId ? ` (ID: ${d.kbId})` : ''} informazioni su: "${d.kbQuery || 'la domanda dell\'utente'}"`);
        lines.push('Usa le informazioni trovate per rispondere.');
        break;
      case 'tool_action':
        lines.push(`Esegui il tool "${d.toolName || 'N/D'}"${d.toolParams ? ` con parametri: ${JSON.stringify(d.toolParams)}` : ''}`);
        lines.push('Comunica il risultato all\'utente in modo chiaro.');
        break;
      case 'condition':
        lines.push(`Controlla la condizione: ${d.condition || 'N/D'}`);
        if (d.trueBranch) lines.push(`- Se VERO: ${d.trueBranch}`);
        if (d.falseBranch) lines.push(`- Se FALSO: ${d.falseBranch}`);
        break;
      case 'ivr_menu':
        lines.push('Presenta le seguenti opzioni:');
        if (d.ivrOptions) {
          for (const opt of d.ivrOptions) {
            lines.push(`- Premi ${opt.dtmf}: ${opt.label}`);
          }
        }
        break;
      case 'context_memory': {
        const action = d.memoryAction || 'save';
        if (action === 'save') {
          lines.push('Salva nella memoria di sessione (tool context_memory_save):');
          lines.push(`- Chiave: "${d.memoryKey || 'contesto'}"`);
          lines.push(`- Valore: "${d.memoryValue || 'informazioni dalla conversazione'}"`);
        } else if (action === 'search') {
          lines.push(`Cerca nella memoria di sessione (tool context_memory_search) informazioni correlate a: "${d.memoryKey || 'contesto corrente'}"`);
          lines.push('Usa i risultati per arricchire le risposte successive.');
        } else if (action === 'recall') {
          lines.push(`Recupera dalla memoria di sessione (tool context_memory_recall) la chiave: "${d.memoryKey || 'contesto'}"`);
        }
        break;
      }
      case 'semantic_memory': {
        const smAction = d.memoryAction || 'save';
        const smScope = d.memoryScope || 'customer';
        const smScopeLabel = smScope === 'customer' ? 'per questo cliente' : smScope === 'agent' ? 'per questo agente' : 'globalmente';
        const smCategory = d.memoryCategory || 'profile';
        if (smAction === 'save') {
          lines.push(`Salva nella memoria a lungo termine ${smScopeLabel} (tool semantic_memory_save):`);
          lines.push(`- Categoria: "${smCategory}"`);
          lines.push(`- Chiave: "${d.memoryKey || 'informazione'}"`);
          lines.push(`- Valore: "${d.memoryValue || 'dato da ricordare'}"`);
        } else if (smAction === 'search') {
          lines.push(`Cerca nella memoria a lungo termine ${smScopeLabel} (tool semantic_memory_search):`);
          lines.push(`- Query: "${d.memoryKey || 'informazione cercata'}"`);
          if (smCategory !== 'profile') lines.push(`- Categoria: "${smCategory}"`);
        } else if (smAction === 'recall') {
          lines.push(`Recupera dalla memoria a lungo termine ${smScopeLabel} (tool semantic_memory_recall):`);
          lines.push(`- Chiave: "${d.memoryKey || 'informazione'}"`);
        }
        break;
      }
      case 'semantic_search': {
        const source = d.searchSource || 'both';
        const sourceLabel = source === 'memory' ? 'nella memoria' : source === 'kb' ? 'nella Knowledge Base' : 'nella memoria e nella Knowledge Base';
        lines.push(`Esegui una ricerca semantica ${sourceLabel}:`);
        lines.push(`- Query: "${d.searchQuery || 'la domanda dell\'utente'}"`);
        if (d.searchMinSimilarity) lines.push(`- Soglia minima di similarita: ${Math.round(d.searchMinSimilarity * 100)}%`);
        if (d.searchMaxResults) lines.push(`- Massimo risultati: ${d.searchMaxResults}`);
        if (d.searchReranking !== false) lines.push('- Reranking abilitato: i risultati vengono riordinati per pertinenza semantica prima dell\'uso.');
        lines.push('Usa i risultati trovati per formulare una risposta precisa e pertinente.');
        break;
      }
      case 'transfer':
        lines.push(`Trasferisci la chiamata a: ${d.transferTarget || 'operatore'} (tipo: ${d.transferType || 'blind'})`);
        break;
      case 'closure':
        lines.push(`Concludi con: "${d.closingMessage || 'Grazie per aver chiamato. Arrivederci!'}"`);
        break;
      case 'google_tool':
        if (d.googleSearchEnabled) lines.push('Usa Google Search per cercare informazioni aggiornate.');
        if (d.codeExecutionEnabled) lines.push('Usa Code Execution se necessario per calcoli.');
        break;
      case 'crm_action': {
        const crmActionLabel = d.crmAction === 'create_lead' ? 'Crea un nuovo lead nel CRM' : d.crmAction === 'create_task' ? 'Crea un task/ticket nel CRM' : 'Aggiorna il contatto nel CRM';
        lines.push(crmActionLabel + ' con i seguenti dati:');
        if (d.crmFields) {
          for (const [key, val] of Object.entries(d.crmFields)) {
            lines.push(`- ${key}: ${val || '(da compilare)'}`);
          }
        }
        lines.push('Conferma all\'utente che l\'operazione e stata completata.');
        break;
      }
      case 'send_message': {
        const channelLabel = d.sendChannel === 'whatsapp' ? 'WhatsApp' : d.sendChannel === 'sms' ? 'SMS' : 'email';
        lines.push(`Invia un messaggio ${channelLabel} al destinatario: ${d.sendTo || '(numero/email dell\'utente)'}`);
        if (d.sendChannel === 'email' && d.sendSubject) lines.push(`- Oggetto: ${d.sendSubject}`);
        if (d.sendBody) lines.push(`- Contenuto: "${d.sendBody}"`);
        lines.push(`Conferma all'utente che il messaggio ${channelLabel} e stato inviato.`);
        break;
      }
      case 'set_variable':
        lines.push(`Imposta la variabile "${d.varName || 'variabile'}" con il valore: ${d.varValue || '(da determinare in base alla conversazione)'}`);
        lines.push('Usa questo valore nei passaggi successivi del flusso.');
        break;
      case 'api_call':
        lines.push(`Effettua una chiamata ${d.apiMethod || 'GET'} all'API: ${d.apiUrl || '(URL non configurato)'}`);
        if (d.apiHeaders && Object.keys(d.apiHeaders).length > 0) {
          lines.push('- Headers:');
          for (const [key, val] of Object.entries(d.apiHeaders)) {
            lines.push(`  - ${key}: ${val}`);
          }
        }
        if (d.apiBody) lines.push(`- Body: ${d.apiBody}`);
        if (d.apiResultVar) lines.push(`- Salva il risultato come: ${d.apiResultVar}`);
        lines.push('Comunica all\'utente il risultato dell\'operazione.');
        break;
    }

    if (d.description) {
      lines.push(`> Nota: ${d.description}`);
    }

    lines.push('');
    stepNum++;

    const outEdges = edgesBySource.get(node!.id!) || [];
    for (const e of outEdges) {
      const target = nodeMap.get(e.target);
      if (target && !visited.has(target.id)) queue.push(target);
    }
  }

  for (const node of nodes) {
    if (!visited.has(node.id)) {
      visited.add(node.id);
      const d = node.data;
      lines.push(`## Passo ${stepNum}: ${d.label}`);
      lines.push(`(Nodo disconnesso) ${d.message || d.toolName || d.condition || ''}`);
      lines.push('');
      stepNum++;
    }
  }

  const usedVars = new Set<string>();
  for (const node of nodes) {
    const d = node.data;
    const texts = [d.message, d.condition, d.trueBranch, d.falseBranch, d.kbQuery, d.memoryKey, d.memoryValue, d.searchQuery, d.transferTarget, d.closingMessage, d.sendTo, d.sendBody, d.sendSubject, d.varValue, d.apiUrl, d.apiBody, ...(d.crmFields ? Object.values(d.crmFields) : []), ...(d.toolParams ? Object.values(d.toolParams) : [])].filter(Boolean);
    for (const text of texts) {
      const matches = text!.matchAll(/\{\{(\w+)\}\}/g)!;
      for (const m of matches) usedVars.add(m[1]!);
    }
  }
  if (usedVars.size > 0 && variableCategories) {
    lines.push('---');
    lines.push('');
    lines.push('# Variabili del flusso');
    lines.push('');
    lines.push('Le variabili `{{...}}` vengono sostituite automaticamente dal sistema con i dati reali prima dell\'esecuzione.');
    lines.push('');
    const allVars = variableCategories.flatMap(c => c.variables);
    for (const varKey of usedVars) {
      const def = allVars.find(v => v.key === varKey);
      if (def) {
        lines.push(`- \`{{${varKey}}}\` → ${def.label}${def.example ? ` (es: "${def.example}")` : ''}`);
      } else {
        lines.push(`- \`{{${varKey}}}\` → variabile raccolta durante la conversazione`);
      }
    }
    lines.push('');
  }

  return lines.join('\n');
}

function VariablePicker({
  onInsert,
  buttonClassName,
  categories,
}: {
  onInsert: (variable: string) => void;
  buttonClassName?: string;
  categories: VariableCategoryItem[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (
        ref.current && !ref.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const dropdownW = 320;
      const dropdownH = 320;
      let left = rect.left;
      let top = rect.bottom + 4;
      if (left + dropdownW > window.innerWidth - 8) {
        left = rect.right - dropdownW;
      }
      if (left < 8) left = 8;
      if (top + dropdownH > window.innerHeight - 8) {
        top = rect.top - dropdownH - 4;
      }
      if (top < 8) top = 8;
      setDropdownPos({ top, left });
    } else {
      setDropdownPos(null);
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!search) return categories;
    const q = search.toLowerCase();
    return categories.map(cat => ({
      ...cat,
      variables: cat.variables.filter(v =>
        v.key.toLowerCase().includes(q) || v.label.toLowerCase().includes(q) || (v.example && v.example.toLowerCase().includes(q))
      ),
    })).filter(cat => cat.variables.length > 0);
  }, [search, categories]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(!isOpen); }}
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border hover:bg-muted text-muted-foreground ${buttonClassName || ''}`}
        title="Inserisci variabile"
      >
        <Variable className="h-3 w-3" />
        <ChevronDown className="h-3 w-3" />
      </button>
      {isOpen && dropdownPos && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-80 bg-card border rounded-lg shadow-xl max-h-80 overflow-hidden flex flex-col"
          style={{ top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca variabile..."
              className="h-7 text-xs"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.map(cat => {
              const CatIcon = cat.icon;
              return (
                <div key={cat.category}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted flex items-center gap-1.5 sticky top-0">
                    <CatIcon className="h-3 w-3" />
                    {cat.category}
                  </div>
                  {cat.variables.map(v => (
                    <button
                      key={v.key}
                      type="button"
                      className="w-full text-left px-3 py-1.5 hover:bg-indigo-50 flex flex-col text-xs group"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onInsert(`{{${v.key}}}`);
                        setIsOpen(false);
                        setSearch('');
                      }}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="text-foreground">{v.label}</span>
                        <code className="text-[10px] text-indigo-500 font-mono opacity-0 group-hover:opacity-100 transition-opacity">{`{{${v.key}}}`}</code>
                      </div>
                      {v.example && (
                        <span className="text-[10px] text-muted-foreground italic mt-0.5">es: {v.example}</span>
                      )}
                    </button>
                  ))}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">Nessuna variabile trovata</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function TextareaWithVariables({
  value,
  onChange,
  placeholder,
  rows,
  label,
  tooltip,
  categories,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  rows?: number;
  label?: string;
  tooltip?: string;
  categories: VariableCategoryItem[];
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInsertVariable = useCallback((variable: string) => {
    const ta = textareaRef.current;
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newVal = value.substring(0, start) + variable + value.substring(end);
      onChange(newVal);
      setTimeout(() => {
        ta.selectionStart = ta.selectionEnd = start + variable.length;
        ta.focus();
      }, 0);
    } else {
      onChange(value + variable);
    }
  }, [value, onChange]);

  return (
    <div>
      {label && (
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1">
            <Label>{label}</Label>
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[250px] text-[11px] leading-relaxed">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <VariablePicker onInsert={handleInsertVariable} categories={categories} />
        </div>
      )}
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows || 3}
      />
    </div>
  );
}

function InputWithVariables({
  value,
  onChange,
  placeholder,
  label,
  tooltip,
  categories,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  label?: string;
  tooltip?: string;
  categories: VariableCategoryItem[];
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInsertVariable = useCallback((variable: string) => {
    const inp = inputRef.current;
    if (inp) {
      const start = inp.selectionStart || 0;
      const end = inp.selectionEnd || 0;
      const newVal = value.substring(0, start) + variable + value.substring(end);
      onChange(newVal);
      setTimeout(() => {
        inp.selectionStart = inp.selectionEnd = start + variable.length;
        inp.focus();
      }, 0);
    } else {
      onChange(value + variable);
    }
  }, [value, onChange]);

  return (
    <div>
      {label && (
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1">
            <Label>{label}</Label>
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[250px] text-[11px] leading-relaxed">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <VariablePicker onInsert={handleInsertVariable} categories={categories} />
        </div>
      )}
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function NodeSidebar({ agentType, onDragStart }: { agentType: AgentType; onDragStart: (type: string) => void }) {
  const availableNodes = NODE_TYPES_CONFIG.filter(n => n.agents.includes(agentType));

  return (
    <div className="w-56 bg-muted border-r border-border p-3 overflow-y-auto">
      <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Nodi</h3>
      <div className="space-y-2">
        {availableNodes.map((nodeConfig) => {
          const Icon = nodeConfig.icon;
          return (
            <div
              key={nodeConfig.type}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/voiceflow-node', nodeConfig.type);
                onDragStart(nodeConfig.type);
              }}
              className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow bg-gradient-to-r ${nodeConfig.color}`}
            >
              <div className={`p-1 ${nodeConfig.iconBg} rounded`}>
                <Icon className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="text-sm font-medium text-foreground">{nodeConfig.label}</span>
              <GripVertical className="h-3.5 w-3.5 text-muted-foreground ml-auto" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const NODE_TOOLTIPS: Record<string, string> = {
  greeting: "Messaggio iniziale che l'agente pronuncia all'inizio della conversazione. Usalo come primo nodo del flusso per accogliere l'utente. Es: 'Buongiorno, sono l'assistente di [azienda], come posso aiutarla?'",
  question: "Pone una domanda all'utente e salva la risposta in una variabile riutilizzabile. Usalo per raccogliere informazioni (nome, esigenza, budget). Es: chiedere 'Qual e il suo nome?' e salvare in {{nome_cliente}}",
  kb_lookup: "Cerca informazioni nella Knowledge Base per rispondere a domande specifiche. Usalo quando l'agente deve consultare documentazione, FAQ o cataloghi caricati. Es: cercare prezzi o disponibilita prodotti.",
  tool_action: "Esegue un tool esterno configurato nell'agente (prenotazioni, verifiche, calcoli). Usalo per integrare azioni concrete durante la conversazione. Es: verificare disponibilita appuntamento con il tool check_availability.",
  condition: "Crea un bivio nel flusso basato su una condizione logica. Usalo per gestire scenari diversi in base alle risposte dell'utente. Es: se il cliente ha confermato → procedi con prenotazione, altrimenti → chiedi chiarimenti.",
  ivr_menu: "Presenta opzioni numeriche (DTMF) che l'utente seleziona premendo tasti. Usalo per flussi telefonici con scelta multipla. Es: 'Premi 1 per assistenza, 2 per vendite, 3 per contabilita.'",
  context_memory: "Memoria di sessione: salva e recupera informazioni durante la conversazione corrente. I dati vengono eliminati automaticamente a fine sessione. Usa i tool context_memory_save/recall/search.",
  semantic_memory: "Memoria a lungo termine: salva fatti e preferenze che persistono tra sessioni e canali diversi. Usa per ricordare nome, azienda, preferenze del cliente. Tool: semantic_memory_save/recall/search.",
  semantic_search: "Cerca informazioni per significato (non parole esatte) nella memoria e/o Knowledge Base. Usalo per trovare contenuti rilevanti anche con domande formulate diversamente. Es: l'utente chiede 'quanto costa' e trova 'listino prezzi'.",
  transfer: "Trasferisce la chiamata a un operatore umano, coda o reparto. Usalo quando l'agente non puo gestire la richiesta autonomamente. Es: trasferire al reparto tecnico per problemi complessi.",
  closure: "Messaggio finale che conclude la conversazione. Usalo come ultimo nodo del flusso per congedare l'utente. Es: 'Grazie per aver chiamato, buona giornata!'",
  crm_action: "Crea lead, task o aggiorna contatti nel CRM aziendale. Usalo per registrare automaticamente i dati raccolti durante la conversazione. Es: creare un lead con nome, telefono ed esigenza del cliente.",
  send_message: "Invia un messaggio WhatsApp, SMS o email durante la conversazione. Usalo per conferme, riepilogo o follow-up. Es: inviare conferma appuntamento via WhatsApp al numero del cliente.",
  set_variable: "Crea o imposta una variabile personalizzata con un valore. Usalo per categorizzare, classificare o etichettare la conversazione. Es: impostare livello_urgenza = 'alta' in base al tono del cliente.",
  api_call: "Effettua una richiesta HTTP a un servizio esterno. Usalo per integrare sistemi terzi (gestionali, webhook, CRM esterni). Es: POST a un webhook per notificare un nuovo ordine.",
  google_tool: "Abilita Google Search e/o Code Execution (solo agenti Gemini). Usalo per cercare informazioni aggiornate online o eseguire calcoli. Es: cercare orari di apertura di un negozio in tempo reale.",
};

const DTMF_OPTIONS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];

const MEMORY_KEY_SUGGESTIONS = [
  { value: 'preferenze_cliente', label: 'preferenze_cliente' },
  { value: 'esito_chiamata', label: 'esito_chiamata' },
  { value: 'note_conversazione', label: 'note_conversazione' },
  { value: 'storico_interazioni', label: 'storico_interazioni' },
  { value: 'contesto', label: 'contesto' },
  { value: 'informazioni_raccolte', label: 'informazioni_raccolte' },
];

const SEARCH_MAX_RESULTS_OPTIONS = [1, 3, 5, 10, 15, 20];

const TRANSFER_TARGET_SUGGESTIONS = [
  { value: 'operatore', label: 'Operatore generico' },
  { value: 'coda assistenza', label: 'Coda Assistenza' },
  { value: 'coda vendite', label: 'Coda Vendite' },
  { value: 'reparto tecnico', label: 'Reparto Tecnico' },
  { value: 'reparto commerciale', label: 'Reparto Commerciale' },
  { value: 'reparto amministrativo', label: 'Reparto Amministrativo' },
];

const CRM_FIELD_OPTIONS = [
  { value: 'nome', label: 'Nome' },
  { value: 'cognome', label: 'Cognome' },
  { value: 'telefono', label: 'Telefono' },
  { value: 'email', label: 'Email' },
  { value: 'azienda', label: 'Azienda' },
  { value: 'indirizzo', label: 'Indirizzo' },
  { value: 'note', label: 'Note' },
  { value: 'fonte', label: 'Fonte' },
  { value: 'valore', label: 'Valore' },
  { value: 'stato', label: 'Stato' },
];

const API_HEADER_OPTIONS = [
  { value: 'Authorization', label: 'Authorization' },
  { value: 'Content-Type', label: 'Content-Type' },
  { value: 'Accept', label: 'Accept' },
  { value: 'X-API-Key', label: 'X-API-Key' },
  { value: 'X-Webhook-Secret', label: 'X-Webhook-Secret' },
];

const TOOL_PARAM_SUGGESTIONS = [
  { value: 'date', label: 'date' },
  { value: 'time', label: 'time' },
  { value: 'name', label: 'name' },
  { value: 'email', label: 'email' },
  { value: 'phone', label: 'phone' },
  { value: 'message', label: 'message' },
  { value: 'id', label: 'id' },
  { value: 'type', label: 'type' },
  { value: 'query', label: 'query' },
  { value: 'service', label: 'service' },
];

const VARIABLE_SUGGESTIONS = [
  { value: 'nome', label: 'nome' },
  { value: 'email', label: 'email' },
  { value: 'telefono', label: 'telefono' },
  { value: 'esigenza', label: 'esigenza' },
  { value: 'budget', label: 'budget' },
  { value: 'conferma', label: 'conferma' },
];

function FieldLabel({ children, tooltip, className }: { children: React.ReactNode; tooltip?: string; className?: string }) {
  return (
    <div className="flex items-center gap-1">
      <Label className={className || 'text-xs text-muted-foreground'}>{children}</Label>
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help flex-shrink-0" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[250px] text-[11px] leading-relaxed">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

function AddItemSelect({
  options,
  onAdd,
  placeholder,
  existingKeys,
}: {
  options: { value: string; label: string }[];
  onAdd: (name: string) => void;
  placeholder: string;
  existingKeys?: string[];
}) {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [selectKey, setSelectKey] = useState(0);

  const filteredOptions = existingKeys
    ? options.filter((opt) => !existingKeys.includes(opt.value))
    : options;

  if (showCustomInput) {
    return (
      <div className="flex gap-1 items-center">
        <Input
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          placeholder="Nome personalizzato..."
          className="flex-1 text-xs"
          autoFocus
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && customValue.trim()) {
              onAdd(customValue.trim());
              setCustomValue('');
              setShowCustomInput(false);
            }
            if (e.key === 'Escape') {
              setShowCustomInput(false);
              setCustomValue('');
            }
          }}
        />
        <Button variant="outline" size="sm" onClick={() => {
          if (customValue.trim()) {
            onAdd(customValue.trim());
            setCustomValue('');
            setShowCustomInput(false);
          }
        }}>
          <Plus className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => { setShowCustomInput(false); setCustomValue(''); }}>
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <Select
      key={selectKey}
      onValueChange={(v) => {
        if (v === '__custom__') {
          setShowCustomInput(true);
        } else {
          onAdd(v);
        }
        setSelectKey((k) => k + 1);
      }}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {filteredOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
        ))}
        <SelectItem value="__custom__">Personalizzato...</SelectItem>
      </SelectContent>
    </Select>
  );
}

function NodePropertyPanel({
  node,
  onUpdate,
  onDelete,
  onClose,
  variableCategories,
  agentTools,
  knowledgeBases,
  transferDestinations,
}: {
  node: Node<FlowNodeData>;
  onUpdate: (data: Partial<FlowNodeData>) => void;
  onDelete: () => void;
  onClose: () => void;
  variableCategories: VariableCategoryItem[];
  agentTools: AgentTool[];
  knowledgeBases: KnowledgeBase[];
  transferDestinations: TransferDestination[];
}) {
  const d = node.data;
  const hasApiDestinations = transferDestinations.length > 0;
  const allTransferOptions = hasApiDestinations
    ? transferDestinations
    : TRANSFER_TARGET_SUGGESTIONS.map(s => ({ ...s, group: 'Suggerimenti' }));
  const isKnownTransferTarget = allTransferOptions.some(s => s.value === d.transferTarget);
  const [transferManualMode, setTransferManualMode] = useState(!isKnownTransferTarget && !!d.transferTarget);
  const [memoryKeyCustom, setMemoryKeyCustom] = useState(!MEMORY_KEY_SUGGESTIONS.some(s => s.value === d.memoryKey) && !!d.memoryKey);

  useEffect(() => {
    setTransferManualMode(!allTransferOptions.some(s => s.value === d.transferTarget) && !!d.transferTarget);
  }, [node.id, d.transferTarget, allTransferOptions]);

  useEffect(() => {
    setMemoryKeyCustom(!MEMORY_KEY_SUGGESTIONS.some(s => s.value === d.memoryKey) && !!d.memoryKey);
  }, [node.id, d.memoryKey]);

  const nodeConfig = NODE_TYPES_CONFIG.find((n) => n.type === d.type);
  const nodeTooltip = NODE_TOOLTIPS[d.type];

  return (
    <TooltipProvider delayDuration={200}>
      <div className="w-80 border-l bg-muted overflow-y-auto p-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-foreground">Proprieta Nodo</h3>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500 hover:text-red-700">
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {nodeTooltip && (
          <div className="mb-4 p-2.5 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs font-semibold text-blue-700">{nodeConfig?.label || d.type}</span>
                <p className="text-[11px] text-blue-600 leading-relaxed mt-0.5">{nodeTooltip}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <FieldLabel tooltip="Il nome identificativo di questo step nel flusso. Apparira sul nodo nel canvas. Es: 'Raccolta Dati', 'Verifica Disponibilita'">Nome nodo</FieldLabel>
            <Input
              value={d.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              placeholder="Nome di questo step"
            />
          </div>

          {(d.type === 'greeting' || d.type === 'question') && (
            <TextareaWithVariables
              value={d.message || ''}
              onChange={(v) => onUpdate({ message: v })}
              rows={3}
              placeholder={d.type === 'greeting' ? 'Ciao {{customer_first_name}}! Sono {{agent_name}}, come posso aiutarti?' : 'Come posso assisterti oggi?'}
              label={d.type === 'greeting' ? 'Cosa dice l\'agente' : 'Domanda da porre'}
              tooltip={d.type === 'greeting'
                ? "Il testo che l'agente pronuncia all'inizio. Puoi usare variabili come {{customer_first_name}}. Es: 'Buongiorno {{customer_first_name}}, come posso aiutarla?'"
                : "La domanda che l'agente fara all'utente. Puoi usare variabili. Es: 'Qual e il suo nome?' o 'Qual e la sua esigenza?'"
              }
              categories={variableCategories}
            />
          )}

          {d.type === 'question' && (
            <div>
              <FieldLabel tooltip="Nome della variabile dove salvare la risposta dell'utente. Usala nei nodi successivi con {{nome_scelto}}. Es: 'esigenza', 'budget', 'conferma'">Salva risposta come</FieldLabel>
              <Select
                value={VARIABLE_SUGGESTIONS.some(s => s.value === d.variable) ? d.variable : '__none__'}
                onValueChange={(v) => {
                  if (v !== '__none__') onUpdate({ variable: v });
                }}
              >
                <SelectTrigger className="mb-1">
                  <SelectValue placeholder="Suggerimenti rapidi..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Suggerimenti rapidi...</SelectItem>
                  {VARIABLE_SUGGESTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={d.variable || ''}
                onChange={(e) => onUpdate({ variable: e.target.value })}
                placeholder="es. nome_cliente, esigenza, budget..."
              />
              <p className="text-[10px] text-muted-foreground mt-1">Usa questo nome nelle variabili dei nodi successivi: {'{{'}nome_scelto{'}}'}</p>
            </div>
          )}

          {d.type === 'kb_lookup' && (
            <>
              <div>
                <FieldLabel tooltip="Seleziona la Knowledge Base in cui cercare. Lascia 'Tutte le KB' per cercare in tutte le basi di conoscenza disponibili.">Knowledge Base</FieldLabel>
                {knowledgeBases.length > 0 ? (
                  <Select value={d.kbId || '__all__'} onValueChange={(v) => onUpdate({ kbId: v === '__all__' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona una Knowledge Base" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Tutte le KB</SelectItem>
                      {knowledgeBases.map((kb) => (
                        <SelectItem key={kb.id} value={kb.id}>{kb.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={d.kbId || ''}
                    onChange={(e) => onUpdate({ kbId: e.target.value })}
                    placeholder="ID della Knowledge Base (opzionale)"
                  />
                )}
              </div>
              <TextareaWithVariables
                value={d.kbQuery || ''}
                onChange={(v) => onUpdate({ kbQuery: v })}
                placeholder="es. {{domanda_utente}} oppure 'prezzi del servizio base'"
                rows={2}
                label="Cosa cercare nella Knowledge Base"
                tooltip="La query di ricerca. Usa variabili come {{domanda_utente}} per cercare in base all'input dell'utente. Es: '{{richiesta_cliente}}' o 'prezzi servizio base'"
                categories={variableCategories}
              />
            </>
          )}

          {d.type === 'tool_action' && (
            <>
              <div>
                <FieldLabel tooltip="Il nome del tool configurato nell'agente AI da eseguire. Seleziona dalla lista o inserisci manualmente. Es: 'check_availability', 'create_appointment'">Tool da eseguire</FieldLabel>
                {agentTools.length > 0 && (
                  <Select value={agentTools.some(t => t.name === d.toolName) ? d.toolName : '__custom__'} onValueChange={(v) => {
                    if (v === '__custom__') return;
                    const selectedTool = agentTools.find(t => t.name === v);
                    const params: Record<string, string> = {};
                    if (selectedTool?.parameters) {
                      for (const key of Object.keys(selectedTool.parameters)) {
                        params[key] = d.toolParams?.[key] || '';
                      }
                    }
                    onUpdate({ toolName: v, toolParams: Object.keys(params).length > 0 ? params : d.toolParams });
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona un tool" />
                    </SelectTrigger>
                    <SelectContent>
                      {agentTools.map((tool) => (
                        <SelectItem key={tool.name} value={tool.name}>
                          <div className="flex flex-col">
                            <span>{tool.name}</span>
                            {tool.description && <span className="text-[10px] text-muted-foreground">{tool.description.substring(0, 60)}</span>}
                          </div>
                        </SelectItem>
                      ))}
                      <SelectItem value="__custom__">Inserimento manuale...</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Input
                  value={d.toolName || ''}
                  onChange={(e) => onUpdate({ toolName: e.target.value })}
                  placeholder="es. check_availability, create_appointment"
                  className={agentTools.length > 0 ? 'mt-1' : ''}
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  {agentTools.length > 0 ? `${agentTools.length} tool disponibili — seleziona o digita manualmente` : 'Inserisci il nome del tool configurato nell\'agente AI'}
                </p>
              </div>
              {d.toolName && (() => {
                const selectedTool = agentTools.find(t => t.name === d.toolName);
                const schemaKeys = selectedTool?.parameters ? Object.keys(selectedTool.parameters) : [];
                const customKeys = Object.keys(d.toolParams || {}).filter(k => !schemaKeys.includes(k));
                const paramKeys = [...schemaKeys, ...customKeys];
                const hasSchemaParams = schemaKeys.length > 0;
                return (
                  <div>
                    {paramKeys.length > 0 && (
                      <>
                        <FieldLabel tooltip="I parametri da passare al tool. Puoi usare variabili raccolte nei nodi precedenti. Es: date = '{{data_preferita}}'">Parametri</FieldLabel>
                        <div className="space-y-2">
                          {paramKeys.map((key) => (
                            <InputWithVariables
                              key={key}
                              value={d.toolParams?.[key] || ''}
                              onChange={(v) => onUpdate({ toolParams: { ...(d.toolParams || {}), [key]: v } })}
                              placeholder={selectedTool?.parameters?.[key]?.description || `Valore per ${key}`}
                              label={key}
                              tooltip={`Valore del parametro '${key}'. Puoi usare variabili come {{nome_variabile}}.`}
                              categories={variableCategories}
                            />
                          ))}
                        </div>
                      </>
                    )}
                    {!hasSchemaParams && (
                      <div className="mt-2">
                        <AddItemSelect
                          options={TOOL_PARAM_SUGGESTIONS}
                          onAdd={(name) => onUpdate({ toolParams: { ...(d.toolParams || {}), [name]: '' } })}
                          placeholder="+ Aggiungi parametro"
                          existingKeys={Object.keys(d.toolParams || {})}
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}

          {d.type === 'condition' && (
            <>
              <TextareaWithVariables
                value={d.condition || ''}
                onChange={(v) => onUpdate({ condition: v })}
                placeholder="es. il cliente ha confermato di voler procedere"
                rows={2}
                label="Quando questa condizione e vera?"
                tooltip="Descrivi la condizione in linguaggio naturale. L'agente AI la valutera durante la conversazione. Es: 'il cliente ha confermato di voler procedere'"
                categories={variableCategories}
              />
              <div>
                <FieldLabel tooltip="Istruzione per l'agente quando la condizione e vera. Es: 'Prosegui con la prenotazione'" className="text-xs text-green-600">Se VERO → cosa fare</FieldLabel>
                <Input
                  value={d.trueBranch || ''}
                  onChange={(e) => onUpdate({ trueBranch: e.target.value })}
                  placeholder="es. Prosegui con la prenotazione"
                />
              </div>
              <div>
                <FieldLabel tooltip="Istruzione per l'agente quando la condizione e falsa. Es: 'Chiedi maggiori informazioni'" className="text-xs text-red-500">Se FALSO → cosa fare</FieldLabel>
                <Input
                  value={d.falseBranch || ''}
                  onChange={(e) => onUpdate({ falseBranch: e.target.value })}
                  placeholder="es. Chiedi maggiori informazioni"
                />
              </div>
            </>
          )}

          {d.type === 'ivr_menu' && (
            <div>
              <FieldLabel tooltip="Le opzioni del menu IVR con i tasti DTMF corrispondenti. Ogni opzione associa un tasto a un'azione. Es: 1 = Assistenza, 2 = Vendite">Opzioni Menu</FieldLabel>
              <div className="space-y-2 mt-2">
                {(d.ivrOptions || []).map((opt, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Select
                      value={opt.dtmf}
                      onValueChange={(v) => {
                        const opts = [...(d.ivrOptions || [])];
                        opts[idx] = { ...opts[idx]!, dtmf: v };
                        onUpdate({ ivrOptions: opts });
                      }}
                    >
                      <SelectTrigger className="w-16">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DTMF_OPTIONS.map((key) => (
                          <SelectItem key={key} value={key}>{key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex-1 relative">
                      <Input
                        className="w-full pr-6"
                        value={opt.label}
                        onChange={(e) => {
                          const opts = [...(d.ivrOptions || [])];
                          opts[idx] = { ...opts[idx]!, label: e.target.value };
                          onUpdate({ ivrOptions: opts });
                        }}
                        placeholder="Etichetta"
                      />
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-[11px]">
                          Descrizione dell'opzione che l'agente legge all'utente. Es: 'Assistenza tecnica', 'Ufficio vendite'
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const opts = (d.ivrOptions || []).filter((_, i) => i !== idx);
                        onUpdate({ ivrOptions: opts });
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    const opts = [...(d.ivrOptions || [])];
                    const usedKeys = new Set(opts.map(o => o.dtmf));
                    const nextKey = DTMF_OPTIONS.find(k => !usedKeys.has(k)) || '1';
                    opts.push({ dtmf: nextKey, label: '' });
                    onUpdate({ ivrOptions: opts });
                  }}
                >
                  + Aggiungi opzione
                </Button>
              </div>
            </div>
          )}

          {d.type === 'context_memory' && (
            <>
              <div className="text-xs text-pink-600 bg-pink-50 p-2 rounded">
                Memoria di sessione: i dati vengono eliminati a fine conversazione. Usa i tool context_memory_save/recall/search.
              </div>
              <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded flex items-center gap-1">
                ⚠️ Assicurati che il toggle "Memoria di Contesto" sia attivo nelle impostazioni dell'agente, altrimenti questo nodo non avrà effetto.
              </div>
              <div>
                <FieldLabel tooltip="L'azione da eseguire sulla memoria di sessione.">Cosa vuoi fare?</FieldLabel>
                <Select value={d.memoryAction || 'save'} onValueChange={(v: string) => onUpdate({ memoryAction: v as FlowNodeData['memoryAction'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="save">Salva informazioni</SelectItem>
                    <SelectItem value="search">Cerca per similarità</SelectItem>
                    <SelectItem value="recall">Recupera per chiave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel tooltip="Chiave identificativa per i dati in memoria.">Chiave</FieldLabel>
                <Input
                  value={d.memoryKey || ''}
                  onChange={(e) => onUpdate({ memoryKey: e.target.value })}
                  placeholder="es. nome_cliente, motivo_chiamata"
                />
              </div>
              {(d.memoryAction || 'save') === 'save' && (
                <TextareaWithVariables
                  value={d.memoryValue || ''}
                  onChange={(v) => onUpdate({ memoryValue: v })}
                  placeholder="es. Il cliente {{customer_first_name}} ha richiesto: {{esigenza}}"
                  rows={3}
                  label="Cosa salvare"
                  tooltip="Il contenuto da salvare nella memoria di sessione."
                  categories={variableCategories}
                />
              )}
            </>
          )}

          {d.type === 'semantic_memory' && (
            <>
              <div className="text-xs text-violet-600 bg-violet-50 p-2 rounded">
                Memoria a lungo termine: persiste tra sessioni e canali diversi. Usa i tool semantic_memory_save/recall/search.
              </div>
              <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded flex items-center gap-1">
                ⚠️ Assicurati che il toggle "Memoria Semantica" sia attivo nelle impostazioni dell'agente, altrimenti questo nodo non avrà effetto.
              </div>
              <div>
                <FieldLabel tooltip="L'azione da eseguire sulla memoria semantica.">Cosa vuoi fare?</FieldLabel>
                <Select value={d.memoryAction || 'save'} onValueChange={(v: string) => onUpdate({ memoryAction: v as FlowNodeData['memoryAction'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="save">Salva informazioni</SelectItem>
                    <SelectItem value="search">Cerca per similarità</SelectItem>
                    <SelectItem value="recall">Recupera per chiave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel tooltip="Ambito: per cliente, per agente, o globale.">Ambito</FieldLabel>
                <Select value={d.memoryScope || 'customer'} onValueChange={(v: string) => onUpdate({ memoryScope: v as FlowNodeData['memoryScope'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Per questo cliente</SelectItem>
                    <SelectItem value="agent">Per questo agente</SelectItem>
                    <SelectItem value="tenant">Globale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel tooltip="Categoria del fatto da memorizzare.">Categoria</FieldLabel>
                <Select value={d.memoryCategory || 'profile'} onValueChange={(v: string) => onUpdate({ memoryCategory: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="profile">Profilo (dati personali)</SelectItem>
                    <SelectItem value="operational">Operativo (processi)</SelectItem>
                    <SelectItem value="behavioral">Comportamentale (preferenze)</SelectItem>
                    <SelectItem value="decisional">Decisionale (scelte fatte)</SelectItem>
                    <SelectItem value="knowledge">Conoscenza (fatti appresi)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel tooltip="Chiave identificativa per il dato.">Chiave</FieldLabel>
                <Input
                  value={d.memoryKey || ''}
                  onChange={(e) => onUpdate({ memoryKey: e.target.value })}
                  placeholder="es. nome, azienda, piano_preferito"
                />
              </div>
              {(d.memoryAction || 'save') === 'save' && (
                <TextareaWithVariables
                  value={d.memoryValue || ''}
                  onChange={(v) => onUpdate({ memoryValue: v })}
                  placeholder="es. {{customer_first_name}} preferisce il contatto via email"
                  rows={3}
                  label="Cosa salvare"
                  tooltip="Il contenuto da salvare nella memoria a lungo termine."
                  categories={variableCategories}
                />
              )}
            </>
          )}

          {d.type === 'semantic_search' && (
            <>
              <TextareaWithVariables
                value={d.searchQuery || ''}
                onChange={(v) => onUpdate({ searchQuery: v })}
                placeholder="es. {{domanda_utente}}"
                rows={2}
                label="Cosa cercare"
                tooltip="La query di ricerca semantica. Cerca per significato, non per parole esatte. Usa variabili come {{domanda_utente}}."
                categories={variableCategories}
              />
              <div>
                <FieldLabel tooltip="La fonte in cui cercare: solo memoria, solo Knowledge Base, o entrambe.">Dove cercare</FieldLabel>
                <Select value={d.searchSource || 'both'} onValueChange={(v: string) => onUpdate({ searchSource: v as FlowNodeData['searchSource'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="memory">Solo nella memoria</SelectItem>
                    <SelectItem value="kb">Solo nella Knowledge Base</SelectItem>
                    <SelectItem value="both">Ovunque (memoria + KB)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel tooltip="La soglia minima di similarita per i risultati. Valori alti = risultati piu precisi ma meno numerosi. Consigliato: 50-70%">Precisione minima ({Math.round((d.searchMinSimilarity || 0.5) * 100)}%)</FieldLabel>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  value={d.searchMinSimilarity || 0.5}
                  onChange={(e) => onUpdate({ searchMinSimilarity: parseFloat(e.target.value) })}
                  className="w-full mt-1 accent-purple-600"
                />
              </div>
              <div>
                <FieldLabel tooltip="Il numero massimo di risultati da restituire. Piu risultati = piu contesto ma piu lento.">Max risultati</FieldLabel>
                <Select
                  value={String(d.searchMaxResults || 5)}
                  onValueChange={(v) => onUpdate({ searchMaxResults: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEARCH_MAX_RESULTS_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {d.type === 'transfer' && (
            <>
              <div>
                <FieldLabel tooltip="La destinazione del trasferimento: interno telefonico, coda, reparto o numero esterno. Es: '1000', 'coda vendite', '+39 02 1234567'">A chi trasferire</FieldLabel>
                {transferManualMode ? (
                  <>
                    <Input
                      value={d.transferTarget || ''}
                      onChange={(e) => onUpdate({ transferTarget: e.target.value })}
                      placeholder="es. 1000, coda vendite, +39 02 1234567"
                    />
                    <button className="text-[10px] text-blue-500 mt-1 hover:underline" onClick={() => setTransferManualMode(false)}>
                      {hasApiDestinations ? 'Torna alla lista interni/code' : 'Torna ai suggerimenti'}
                    </button>
                  </>
                ) : (
                  <Select
                    value={isKnownTransferTarget ? d.transferTarget : '__manual__'}
                    onValueChange={(v) => {
                      if (v === '__manual__') {
                        setTransferManualMode(true);
                      } else {
                        onUpdate({ transferTarget: v });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={hasApiDestinations ? 'Seleziona interno o coda...' : 'Seleziona destinazione...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const groups = new Map<string, TransferDestination[]>();
                        for (const opt of allTransferOptions) {
                          if (!groups.has(opt.group)) groups.set(opt.group, []);
                          groups.get(opt.group)!.push(opt)!;
                        }
                        return Array.from(groups.entries()).map(([group, items]) => (
                          <div key={group}>
                            <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase">{group}</div>
                            {items.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </div>
                        ));
                      })()}
                      <SelectItem value="__manual__">Inserimento manuale...</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <p className="text-[10px] text-muted-foreground mt-1">Interno, coda o numero esterno</p>
              </div>
              <div>
                <FieldLabel tooltip="Il tipo di trasferimento: diretto (blind), con annuncio all'operatore (attended), o in coda di attesa.">Come trasferire</FieldLabel>
                <Select value={d.transferType || 'blind'} onValueChange={(v) => onUpdate({ transferType: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blind">Trasferimento diretto</SelectItem>
                    <SelectItem value="attended">Con annuncio (attended)</SelectItem>
                    <SelectItem value="queue">In coda di attesa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {d.type === 'closure' && (
            <TextareaWithVariables
              value={d.closingMessage || ''}
              onChange={(v) => onUpdate({ closingMessage: v })}
              rows={3}
              placeholder="Grazie per aver chiamato {{customer_first_name}}! Buona giornata!"
              label="Messaggio di chiusura"
              tooltip="Il messaggio finale che l'agente pronuncia per congedare l'utente. Puoi usare variabili. Es: 'Grazie {{customer_first_name}}, buona giornata!'"
              categories={variableCategories}
            />
          )}

          {d.type === 'google_tool' && (
            <>
              <div className="flex items-center justify-between">
                <FieldLabel tooltip="Abilita la ricerca Google per trovare informazioni aggiornate in tempo reale durante la conversazione.">Google Search</FieldLabel>
                <Switch
                  checked={d.googleSearchEnabled ?? true}
                  onCheckedChange={(v) => onUpdate({ googleSearchEnabled: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <FieldLabel tooltip="Abilita l'esecuzione di codice per calcoli, conversioni o elaborazioni durante la conversazione.">Code Execution</FieldLabel>
                <Switch
                  checked={d.codeExecutionEnabled ?? false}
                  onCheckedChange={(v) => onUpdate({ codeExecutionEnabled: v })}
                />
              </div>
            </>
          )}

          {d.type === 'crm_action' && (
            <>
              <div>
                <FieldLabel tooltip="Il tipo di operazione da eseguire nel CRM: creare un nuovo lead, un task/ticket, o aggiornare un contatto esistente.">Azione CRM</FieldLabel>
                <Select value={d.crmAction || 'create_lead'} onValueChange={(v: string) => onUpdate({ crmAction: v as FlowNodeData['crmAction'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create_lead">Crea Lead</SelectItem>
                    <SelectItem value="create_task">Crea Task/Ticket</SelectItem>
                    <SelectItem value="update_contact">Aggiorna Contatto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel tooltip="I campi dati da inviare al CRM. Usa variabili per i valori raccolti durante la conversazione. Es: nome = '{{customer_first_name}}'">Campi CRM</FieldLabel>
                <div className="space-y-2 mt-1">
                  {Object.entries(d.crmFields || {}).map(([key, val]) => (
                    <div key={key} className="flex gap-1 items-start">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Input
                            className="w-24 text-xs cursor-help"
                            value={key}
                            readOnly
                            title={key}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[200px] text-[11px]">
                          {`Campo CRM '${key}'. Il valore verra inviato al CRM con questo nome.`}
                        </TooltipContent>
                      </Tooltip>
                      <div className="flex-1">
                        <InputWithVariables
                          value={val}
                          onChange={(v) => onUpdate({ crmFields: { ...(d.crmFields || {}), [key]: v } })}
                          placeholder={`Valore per ${key}`}
                          tooltip={`Valore del campo '${key}' da inviare al CRM. Puoi usare variabili come {{customer_first_name}}.`}
                          categories={variableCategories}
                        />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => {
                        const fields = { ...(d.crmFields || {}) };
                        delete fields[key];
                        onUpdate({ crmFields: fields });
                      }}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <AddItemSelect
                    options={CRM_FIELD_OPTIONS}
                    onAdd={(name) => onUpdate({ crmFields: { ...(d.crmFields || {}), [name]: '' } })}
                    placeholder="+ Aggiungi campo"
                    existingKeys={Object.keys(d.crmFields || {})}
                  />
                </div>
              </div>
            </>
          )}

          {d.type === 'send_message' && (
            <>
              <div>
                <FieldLabel tooltip="Il canale di invio del messaggio: WhatsApp, SMS o Email.">Canale</FieldLabel>
                <Select value={d.sendChannel || 'whatsapp'} onValueChange={(v: string) => onUpdate({ sendChannel: v as FlowNodeData['sendChannel'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <InputWithVariables
                value={d.sendTo || ''}
                onChange={(v) => onUpdate({ sendTo: v })}
                placeholder={d.sendChannel === 'email' ? 'es. {{customer_email}}' : 'es. {{caller_number}}'}
                label="Destinatario"
                tooltip="Il numero di telefono o email del destinatario. Usa variabili come {{caller_number}} o {{customer_email}}."
                categories={variableCategories}
              />
              {d.sendChannel === 'email' && (
                <InputWithVariables
                  value={d.sendSubject || ''}
                  onChange={(v) => onUpdate({ sendSubject: v })}
                  placeholder="es. Conferma appuntamento"
                  label="Oggetto"
                  tooltip="L'oggetto dell'email. Solo per il canale Email. Es: 'Conferma appuntamento del {{appointment_date}}'"
                  categories={variableCategories}
                />
              )}
              <TextareaWithVariables
                value={d.sendBody || ''}
                onChange={(v) => onUpdate({ sendBody: v })}
                placeholder="es. Gentile {{customer_first_name}}, le confermiamo..."
                rows={3}
                label="Contenuto messaggio"
                tooltip="Il testo del messaggio da inviare. Puoi usare variabili. Es: 'Gentile {{customer_first_name}}, le confermiamo...'"
                categories={variableCategories}
              />
            </>
          )}

          {d.type === 'set_variable' && (
            <>
              <div>
                <FieldLabel tooltip="Il nome della variabile da creare o impostare. Usala nei nodi successivi con {{nome_scelto}}. Es: 'livello_urgenza', 'categoria_richiesta'">Nome variabile</FieldLabel>
                <Input
                  value={d.varName || ''}
                  onChange={(e) => onUpdate({ varName: e.target.value })}
                  placeholder="es. livello_urgenza, categoria_richiesta"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Usa {'{{'}nome_scelto{'}} '}nei nodi successivi</p>
              </div>
              <TextareaWithVariables
                value={d.varValue || ''}
                onChange={(v) => onUpdate({ varValue: v })}
                placeholder="es. alta, oppure {{feedback_cliente}}"
                rows={2}
                label="Valore"
                tooltip="Il valore da assegnare alla variabile. Puo essere un testo fisso o contenere variabili. Es: 'alta', '{{feedback_cliente}}'"
                categories={variableCategories}
              />
            </>
          )}

          {d.type === 'api_call' && (
            <>
              <div>
                <FieldLabel tooltip="Il metodo della richiesta HTTP: GET per leggere, POST per creare, PUT per aggiornare, DELETE per eliminare.">Metodo HTTP</FieldLabel>
                <Select value={d.apiMethod || 'GET'} onValueChange={(v: string) => onUpdate({ apiMethod: v as FlowNodeData['apiMethod'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="PUT">PUT</SelectItem>
                    <SelectItem value="DELETE">DELETE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <InputWithVariables
                value={d.apiUrl || ''}
                onChange={(v) => onUpdate({ apiUrl: v })}
                placeholder="es. https://api.esempio.it/webhook"
                label="URL"
                tooltip="L'indirizzo completo dell'API da chiamare. Puoi usare variabili. Es: 'https://api.esempio.it/webhook'"
                categories={variableCategories}
              />
              <div>
                <FieldLabel tooltip="Gli header HTTP della richiesta. Necessari per autenticazione e formato dati. Es: Authorization = 'Bearer token123'">Headers</FieldLabel>
                <div className="space-y-2 mt-1">
                  {Object.entries(d.apiHeaders || {}).map(([key, val]) => (
                    <div key={key} className="flex gap-1 items-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Input
                            className="w-24 text-xs cursor-help"
                            value={key}
                            readOnly
                            title={key}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[200px] text-[11px]">
                          {`Header HTTP '${key}'. Verra inviato come header nella richiesta.`}
                        </TooltipContent>
                      </Tooltip>
                      <div className="flex-1 relative">
                        <Input
                          className="w-full pr-6 text-xs"
                          value={val}
                          onChange={(e) => onUpdate({ apiHeaders: { ...(d.apiHeaders || {}), [key]: e.target.value } })}
                          placeholder="Valore"
                        />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[220px] text-[11px]">
                            {`Valore dell'header '${key}'. Es: 'Bearer token123', 'application/json'`}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => {
                        const headers = { ...(d.apiHeaders || {}) };
                        delete headers[key];
                        onUpdate({ apiHeaders: headers });
                      }}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <AddItemSelect
                    options={API_HEADER_OPTIONS}
                    onAdd={(name) => onUpdate({ apiHeaders: { ...(d.apiHeaders || {}), [name]: '' } })}
                    placeholder="+ Aggiungi header"
                    existingKeys={Object.keys(d.apiHeaders || {})}
                  />
                </div>
              </div>
              {(d.apiMethod === 'POST' || d.apiMethod === 'PUT') && (
                <TextareaWithVariables
                  value={d.apiBody || ''}
                  onChange={(v) => onUpdate({ apiBody: v })}
                  placeholder='es. {"nome": "{{nome_contatto}}", "telefono": "{{caller_number}}"}'
                  rows={3}
                  label="Body (JSON)"
                  tooltip={'Il corpo della richiesta in formato JSON. Puoi usare variabili. Es: {"nome": "{{nome_contatto}}"}'}
                  categories={variableCategories}
                />
              )}
              <div>
                <FieldLabel tooltip="Nome variabile dove salvare la risposta dell'API. Usala nei nodi successivi con {{nome_scelto}}. Es: 'api_response'">Salva risultato come</FieldLabel>
                <Input
                  value={d.apiResultVar || ''}
                  onChange={(e) => onUpdate({ apiResultVar: e.target.value })}
                  placeholder="es. api_response"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Opzionale: usa {'{{'}nome{'}} '}nei nodi successivi</p>
              </div>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

function TemplateDialog({
  onSelect,
  onClose,
}: {
  onSelect: (template: FlowTemplate) => void;
  onClose: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-8" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutTemplate className="h-5 w-5 text-indigo-600" />
            <h3 className="font-semibold text-foreground">Template Flow</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
          <p className="text-sm text-muted-foreground mb-4">Seleziona un template per creare un flusso pre-configurato. Il template sostituira il flusso corrente.</p>
          <div className="grid grid-cols-2 gap-4">
            {FLOW_TEMPLATES.map((tmpl) => {
              const Icon = tmpl.icon;
              return (
                <button
                  key={tmpl.id}
                  className="text-left p-4 rounded-xl border-2 border-border hover:border-indigo-400 hover:shadow-lg transition-all group"
                  onClick={() => onSelect(tmpl)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 ${tmpl.color} rounded-lg`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-foreground group-hover:text-indigo-700">{tmpl.name}</div>
                      <div className="text-xs text-muted-foreground">{tmpl.nodes.length} nodi, {tmpl.edges.length} connessioni</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-2">{tmpl.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {tmpl.nodes.map((n) => {
                      const cfg = getNodeConfig(n.data.type);
                      return (
                        <span
                          key={n.id}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full bg-gradient-to-r ${cfg?.color || 'from-gray-50 to-gray-100 border-border'} border font-medium text-foreground`}
                        >
                          {cfg && <cfg.icon className="h-2.5 w-2.5" />}
                          {n.data.label}
                        </span>
                      );
                    })}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function FlowBuilderInner({
  agentType,
  currentPrompt,
  flowDefinition,
  onSave,
  onClose,
  agentName,
  agentId,
}: Omit<VoiceAgentFlowBuilderProps, 'isOpen'>) {
  const { toast } = useToast();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const { categories: variableCategories } = useAvailableVariables();
  const { tools: agentTools } = useAgentTools(agentType, agentId);
  const { knowledgeBases } = useKnowledgeBases();
  const { destinations: transferDestinations } = useTransferDestinations();

  useEffect(() => {
    const existingOverlays = Array.from(document.querySelectorAll('[data-radix-portal]'));
    existingOverlays.forEach((el) => {
      (el as HTMLElement).style.setProperty('display', 'none', 'important');
      (el as HTMLElement).setAttribute('data-flow-hidden', 'true');
    });
    document.body.style.pointerEvents = 'auto';
    document.body.style.overflow = 'hidden';

    return () => {
      document.querySelectorAll('[data-flow-hidden="true"]').forEach((el) => {
        (el as HTMLElement).style.removeProperty('display');
        (el as HTMLElement).removeAttribute('data-flow-hidden');
      });
      document.body.style.removeProperty('pointer-events');
      document.body.style.removeProperty('overflow');
    };
  }, []);
  const { screenToFlowPosition } = useReactFlow();

  const initialNodes = useMemo(() => {
    if (flowDefinition?.nodes?.length) return flowDefinition.nodes;
    return [{
      id: 'greeting_1',
      type: 'flowNode',
      position: { x: 300, y: 50 },
      data: {
        type: 'greeting',
        label: 'Saluto Iniziale',
        message: 'Benvenuto! Come posso aiutarti?',
      } as FlowNodeData,
    }];
  }, [flowDefinition]);

  const initialEdges = useMemo(() => {
    if (flowDefinition?.edges?.length) return flowDefinition.edges;
    return [];
  }, [flowDefinition]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node<FlowNodeData> | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions }, eds));
      setHasChanges(true);
    },
    [setEdges]
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node as Node<FlowNodeData>);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const nodeType = event.dataTransfer.getData('application/voiceflow-node');
      if (!nodeType) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      position.x -= 90;
      position.y -= 30;

      const config = getNodeConfig(nodeType);
      if (!config) return;

      const newNode: Node<FlowNodeData> = {
        id: `${nodeType}_${Date.now()}`,
        type: 'flowNode',
        position,
        data: {
          type: nodeType,
          label: config.label,
          message: nodeType === 'greeting' ? 'Benvenuto!' : undefined,
          ivrOptions: nodeType === 'ivr_menu' ? [{ dtmf: '1', label: 'Opzione 1' }] : undefined,
          closingMessage: nodeType === 'closure' ? 'Grazie per aver chiamato!' : undefined,
          googleSearchEnabled: nodeType === 'google_tool' ? true : undefined,
          transferType: nodeType === 'transfer' ? 'blind' : undefined,
          memoryAction: (nodeType === 'context_memory' || nodeType === 'semantic_memory') ? 'save' : undefined,
          memoryScope: nodeType === 'context_memory' ? 'run' : nodeType === 'semantic_memory' ? 'customer' : undefined,
          memoryRetention: nodeType === 'context_memory' ? 'session' : undefined,
          memoryCategory: nodeType === 'semantic_memory' ? 'profile' : undefined,
          searchSource: nodeType === 'semantic_search' ? 'both' : undefined,
          searchMinSimilarity: nodeType === 'semantic_search' ? 0.5 : undefined,
          searchReranking: nodeType === 'semantic_search' ? true : undefined,
          searchMaxResults: nodeType === 'semantic_search' ? 5 : undefined,
          crmAction: nodeType === 'crm_action' ? 'create_lead' : undefined,
          crmFields: nodeType === 'crm_action' ? { nome: '', telefono: '', email: '' } : undefined,
          sendChannel: nodeType === 'send_message' ? 'whatsapp' : undefined,
          sendTo: nodeType === 'send_message' ? '{{caller_number}}' : undefined,
          varName: nodeType === 'set_variable' ? '' : undefined,
          varValue: nodeType === 'set_variable' ? '' : undefined,
          apiMethod: nodeType === 'api_call' ? 'POST' : undefined,
          apiUrl: nodeType === 'api_call' ? '' : undefined,
        },
      };

      setNodes((nds) => [...nds, newNode]);
      setHasChanges(true);
    },
    [setNodes, screenToFlowPosition]
  );

  const handleLoadTemplate = useCallback((template: FlowTemplate) => {
    if (hasChanges && !window.confirm('Hai modifiche non salvate. Sostituire il flusso corrente con il template selezionato?')) {
      return;
    }
    const ts = Date.now();
    const newNodes = template.nodes.map((n, i) => ({
      ...n,
      id: `${n.id}_${ts}`,
    }));
    const idMap = new Map(template.nodes.map((n, i) => [n.id, `${n.id}_${ts}`]));
    const newEdges = template.edges.map((e) => ({
      ...e,
      id: `${e.id}_${ts}`,
      source: idMap.get(e.source) || e.source,
      target: idMap.get(e.target) || e.target,
    }));
    setNodes(newNodes);
    setEdges(newEdges);
    setHasChanges(true);
    setShowTemplates(false);
    setSelectedNode(null);
    toast({ title: 'Template caricato', description: `Flow "${template.name}" caricato con ${template.nodes.length} nodi.` });
  }, [setNodes, setEdges, toast, hasChanges]);

  const handleUpdateNode = useCallback((data: Partial<FlowNodeData>) => {
    if (!selectedNode) return;
    const updatedData = { ...selectedNode.data, ...data };
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === selectedNode.id) {
          return { ...n, data: updatedData };
        }
        return n;
      })
    );
    setSelectedNode((prev) => (prev ? { ...prev, data: updatedData } : null));
    setHasChanges(true);
  }, [selectedNode, setNodes]);

  const handleDeleteNode = useCallback(() => {
    if (!selectedNode) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setSelectedNode(null);
    setHasChanges(true);
  }, [selectedNode, setNodes, setEdges]);

  const handleSave = useCallback(() => {
    const prompt = flowToPrompt(nodes, edges, variableCategories);
    const flowDef: FlowDefinition = {
      nodes,
      edges,
      version: (flowDefinition?.version || 0) + 1,
    };
    onSave(prompt, flowDef);
    setHasChanges(false);
    toast({ title: 'Flow salvato', description: 'Il prompt e stato generato dal flusso.' });
  }, [nodes, edges, flowDefinition, onSave, toast, variableCategories]);

  const handleClose = useCallback(() => {
    if (hasChanges) {
      if (!window.confirm('Hai modifiche non salvate. Vuoi davvero chiudere?')) return;
    }
    onClose();
  }, [hasChanges, onClose]);

  const generatedPrompt = useMemo(() => flowToPrompt(nodes, edges, variableCategories), [nodes, edges, variableCategories]);

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex" style={{ isolation: 'isolate' }}>
      <div className="flex flex-col w-full h-full bg-card">
        <div className="flex items-center justify-between px-4 py-2 border-b bg-gradient-to-r from-indigo-50 to-violet-50">
          <div className="flex items-center gap-3">
            <GitBranch className="h-5 w-5 text-indigo-600" />
            <div>
              <h2 className="font-semibold text-foreground">
                Flow Builder{agentName ? `: ${agentName}` : ''}
              </h2>
              <p className="text-xs text-muted-foreground">
                Trascina i nodi dalla sidebar, collegali per definire il flusso conversazionale
              </p>
            </div>
            <Badge variant="outline" className="text-xs capitalize">{agentType}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTemplates(true)}
            >
              <LayoutTemplate className="h-4 w-4 mr-1" />
              Template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPromptPreview(!showPromptPreview)}
            >
              <FileText className="h-4 w-4 mr-1" />
              {showPromptPreview ? 'Nascondi' : 'Anteprima'} Prompt
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!hasChanges && !!flowDefinition}>
              <Save className="h-4 w-4 mr-1" />
              Salva Flow
            </Button>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <NodeSidebar agentType={agentType} onDragStart={() => {}} />

          <div className="flex-1 relative" ref={reactFlowWrapper}>
            {showPromptPreview && (
              <div className="absolute top-2 right-2 z-10 w-96 max-h-[60vh] overflow-y-auto bg-card border rounded-lg shadow-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Prompt Generato</h4>
                  <Button variant="ghost" size="sm" onClick={() => setShowPromptPreview(false)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <pre className="text-xs text-foreground whitespace-pre-wrap font-mono bg-muted p-3 rounded">
                  {generatedPrompt || '(nessun nodo)'}
                </pre>
              </div>
            )}
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={(changes) => { onNodesChange(changes); setHasChanges(true); }}
              onEdgesChange={(changes) => { onEdgesChange(changes); setHasChanges(true); }}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              onDragOver={onDragOver}
              onDrop={onDrop}
              nodeTypes={nodeTypes}
              defaultEdgeOptions={defaultEdgeOptions}
              fitView
              fitViewOptions={{ padding: 0.3 }}
              deleteKeyCode={null}
            >
              <Background color="#e5e7eb" gap={20} />
              <Controls />
              <MiniMap
                nodeColor={(node) => {
                  const cfg = getNodeConfig(node.data?.type);
                  return cfg?.mapColor || '#6366f1';
                }}
                style={{ border: '1px solid #e5e7eb' }}
              />
              <Panel position="bottom-left" className="bg-white/90 backdrop-blur border rounded-lg p-3 shadow-lg">
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Nodi: <strong>{nodes.length}</strong> | Connessioni: <strong>{edges.length}</strong></div>
                  {hasChanges && (
                    <div className="flex items-center gap-1 text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      Modifiche non salvate
                    </div>
                  )}
                </div>
              </Panel>
            </ReactFlow>
          </div>

          {selectedNode && (
            <div className="w-96 border-l bg-card overflow-y-auto">
              <NodePropertyPanel
                node={selectedNode}
                onUpdate={handleUpdateNode}
                onDelete={handleDeleteNode}
                onClose={() => setSelectedNode(null)}
                variableCategories={variableCategories}
                agentTools={agentTools}
                knowledgeBases={knowledgeBases}
                transferDestinations={transferDestinations}
              />
            </div>
          )}
        </div>
      </div>
      {showTemplates && (
        <TemplateDialog
          onSelect={handleLoadTemplate}
          onClose={() => setShowTemplates(false)}
        />
      )}
    </div>
  );
}

export default function VoiceAgentFlowBuilder(props: VoiceAgentFlowBuilderProps) {
  if (!props.isOpen) return null;

  return createPortal(
    <ReactFlowProvider>
      <div className="fixed inset-0 z-[60] bg-background overflow-auto">
        <DesktopOnlyOverlay
          title="Voice Agent Flow Builder"
          description="L'editor del flusso vocale richiede uno schermo più grande. Aprilo da tablet o desktop, oppure forza la visualizzazione."
        >
          <FlowBuilderInner {...props} />
        </DesktopOnlyOverlay>
      </div>
    </ReactFlowProvider>,
    document.body
  );
}
